/**
 * Workflow Node Registry — Single Source of Truth
 *
 * Zod schemas for each node type's Omega-format parameters + UI metadata.
 * Everything else derives from this registry:
 * - NODE_DEFINITIONS / ADDABLE_NODE_DEFINITIONS for the NodePicker UI
 * - buildNodeCatalogForPrompt() for the AI system prompt
 * - getAddableNodeTypes() for Gemini tool schemas
 * - Validation helpers for graph operations
 */

import type { TFunction } from "i18next";
import { z } from "zod/v3";
import {
  AGENCY_ZOOM_CUSTOMER_STATUS_VALUES,
  AGENCY_ZOOM_ORDER_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-customers/customerSearchOptions";
import {
  AGENCY_ZOOM_LEAD_SOLD_PERIOD_VALUES,
  AGENCY_ZOOM_LEAD_SORT_VALUES,
  AGENCY_ZOOM_LEAD_STATUS_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-leads/leadSearchOptions";
import {
  AGENCY_ZOOM_TASK_CATEGORY_VALUES,
  AGENCY_ZOOM_TASK_PERIOD_VALUES,
  AGENCY_ZOOM_TASK_STATUS_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-tasks/taskSearchOptions";
import { AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES } from "@/components/workflows/custom-nodes/agency-zoom/create-task/taskCreateOptions";
import { AGENCY_ZOOM_MARITAL_STATUS_VALUES } from "@/components/workflows/custom-nodes/agency-zoom/create-lead/leadCreateOptions";
import { NODE_TYPE_KEYS } from "@/components/workflows/custom-nodes/nodeTypes";
import { CONTENT_FORMATS } from "@/components/workflows/custom-nodes/web-search/webSearchConstants";
import { injectParentObjectFields } from "@/components/workflows/utils/helpers/injectParentObjectFields";
import type { IconName } from "@fortawesome/fontawesome-common-types";

// =============================================================================
// OmegaParameter Zod Schemas
// =============================================================================

/** Literal value: { type: "literal", value: T } */
const omegaLiteral = <T extends z.ZodType>(valueSchema: T) =>
  z.object({ type: z.literal("literal"), value: valueSchema });

/** CEL expression reference: { type: "cel", value: string } */
const omegaCel = z.object({ type: z.literal("cel"), value: z.string() });

/**
 * Sensitive `[SecretField]` input: Omega advertises these as cel-only and
 * rejects plaintext literals at publish time (the value must reference a
 * `secret.*`). The builder enforces the same via the secret picker + validation.
 */
const omegaSecret = omegaCel;

/** String template with embedded expressions: { type: "template", value: string } */
const omegaTemplate = z.object({
  type: z.literal("template"),
  value: z.string(),
});

/** OmegaParameter<T> — union of literal, cel, and template */
const omegaParam = <T extends z.ZodType>(valueSchema: T) =>
  z.union([omegaLiteral(valueSchema), omegaCel, omegaTemplate]);

/** OmegaParameter<string> shorthand */
const omegaString = omegaParam(z.string());

/** OmegaParameter<string> for fields Omega validates as uuid */
const omegaUuid = omegaParam(z.string().uuid());

/** Literal-only UUID — design-time IDs Omega rejects as CEL at compile time */
const omegaLiteralUuid = omegaLiteral(z.string().uuid());

/** OmegaParameter<number> shorthand */
const omegaNumber = omegaParam(z.number());

/** OmegaParameter<number> with SFTP default port (22) and valid port constraints */
const omegaSftpPort = omegaParam(z.number().int().min(1).max(65535).default(22));

/** Omega FilePointer literal schema — UI forces CEL mode; this default is a placeholder for draft saves */
const omegaFilePointer = omegaParam(
  z
    .object({ storage_path: z.string(), media_type: z.string() })
    .default({ storage_path: "", media_type: "" })
);

/** Shared SFTP connection parameters schema (host, port, username, remotePath, password, privateKey) */
const sftpConnectionSchema = {
  host: omegaString,
  port: omegaSftpPort,
  username: omegaString,
  remotePath: omegaString,
  password: omegaSecret.optional(),
  privateKey: omegaSecret.optional(),
};

/** Every AgencyZoom id is an integer parameter. */
const omegaAgencyZoomInt = omegaParam(z.number().int());

/**
 * A required AgencyZoom picker id. `0` is the picker's "nothing selected", which
 * Omega's `MustSelect` rejects, so it never passes here either.
 */
const omegaAgencyZoomSelectedId = omegaParam(z.number().int().min(1));

/**
 * An optional assignment id. Omega drops a `0` from the upstream request, so
 * only a negative is wrong.
 */
const omegaAgencyZoomAssignmentId = omegaParam(z.number().int().min(0));

/** Per-item parameters, or one whole-list CEL expression. */
const omegaIdList = z.union([z.array(omegaAgencyZoomInt), omegaCel]);

/** Custom fields; `fieldValue` is always an array. */
const omegaAgencyZoomCustomFields = z.union([
  z.array(
    omegaParam(
      z.object({ fieldName: z.string(), fieldValue: z.array(z.string()) })
    )
  ),
  omegaCel,
]);

/**
 * Parameters shared by both AgencyZoom create-lead tiles, all optional.
 * `firstname`, `pipelineId` and `stageId` are absent: each tile declares its own.
 */
const agencyZoomLeadSharedShape = {
  email: omegaString.optional(),
  country: omegaString.optional(),
  leadSourceId: omegaAgencyZoomAssignmentId.optional(),
  assignTo: omegaAgencyZoomAssignmentId.optional(),
  lastname: omegaString.optional(),
  middlename: omegaString.optional(),
  nickname: omegaString.optional(),
  phone: omegaString.optional(),
  secondaryEmail: omegaString.optional(),
  secondaryPhone: omegaString.optional(),
  streetAddress: omegaString.optional(),
  streetAddressLine2: omegaString.optional(),
  city: omegaString.optional(),
  state: omegaString.optional(),
  zip: omegaString.optional(),
  birthday: omegaString.optional(),
  notes: omegaString.optional(),
  agencyNumber: omegaString.optional(),
  departmentCode: omegaString.optional(),
  groupCode: omegaString.optional(),
  tagNames: omegaString.optional(),
  contactDate: omegaString.optional(),
  soldDate: omegaString.optional(),
  quoteDate: omegaString.optional(),
  xDate: omegaString.optional(),
  nextExpirationDate: omegaString.optional(),
  maritalStatus: omegaParam(z.enum(AGENCY_ZOOM_MARITAL_STATUS_VALUES)).optional(),
  csrId: omegaAgencyZoomAssignmentId.optional(),
  assignmentGroupId: omegaAgencyZoomAssignmentId.optional(),
  otherProducers: omegaIdList.optional(),
  otherCsrs: omegaIdList.optional(),
  customFields: omegaAgencyZoomCustomFields.optional(),
};

/**
 * Canonical `is_success` boolean output produced by every failure-route node.
 * Spread or reference directly: `successFlagOutput`.
 */
const successFlagOutput: NodeOutputField = { name: "is_success", type: "boolean" };

/**
 * The outputs of every AgencyZoom tile: the success flag, the result triple, and
 * the error details on failure. Only the create-lead entries use it so far.
 */
const AGENCY_ZOOM_OUTCOME_OUTPUTS: NodeOutputField[] = [
  successFlagOutput,
  { name: "data.message", type: "string" },
  { name: "data.id", type: "number" },
  { name: "data.result", type: "boolean" },
  { name: "data.type", type: "string" },
  { name: "data.title", type: "string" },
  { name: "data.status", type: "number" },
  { name: "data.detail", type: "string" },
  { name: "data.instance", type: "string" },
];

const AGENCY_ZOOM_LEAD_CONNECTION_NOTE =
  "Requires an active AgencyZoom connection for the organization.";

/**
 * The prompt notes both create-lead tiles share. What differs — the required
 * fields and the commercial additions — stays inline at each entry.
 */
const AGENCY_ZOOM_LEAD_SHARED_PROMPT_NOTES: string[] = [
  "maritalStatus is snake_case: single, married, widowed, separated, divorced, domestic_partner, civil_union, fiance, unknown, other.",
  "tagNames is ONE string with multiple tags separated by ';'.",
  "Date fields (birthday, contactDate, soldDate, quoteDate, xDate, nextExpirationDate) are free text — AgencyZoom validates the format.",
  "customFields items are { fieldName, fieldValue } where fieldName is an AgencyZoom slug (e.g. cf1) and fieldValue is always an array of strings; a single CEL expression can supply the whole list instead.",
  "otherProducers and otherCsrs are integer id lists, or a single CEL expression resolving to number[].",
  "On success data.id is the new lead's AgencyZoom id.",
];

/**
 * Standard `data.error` + `data.detail` failure outputs for nodes whose
 * failure branch exposes both a node-specific error and an RFC 7807 detail.
 * Spread into an `outputs` array: `...failureErrorOutputs`.
 */
const failureErrorOutputs: NodeOutputField[] = [
  { name: "data.error", type: "string" },
  { name: "data.detail", type: "string" },
];

/** Omega's Map.MaxItems — hard cap on the items list. */
const MAP_MAX_ITEMS = 10_000;

/**
 * Mirror of Omega's `CelFieldName.Normalize`: separators → underscores, strip
 * non-alphanumerics, snake_case, collapse/trim underscores. Used to flag
 * projection field names that normalize to empty or collide — the same checks
 * Omega runs at publish time.
 */
function normalizeCelFieldName(name: string): string {
  const snaked = name
    .replace(/[\s\-.]+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    // Acronym boundary (e.g. "HTTPRequest" → "HTTP_Request"). A non-consuming
    // lookahead avoids the `[A-Z]+[A-Z]` quantifier overlap that makes the
    // naive form super-linear (ReDoS-prone).
    .replace(/([A-Z])(?=[A-Z][a-z])/g, "$1_")
    .toLowerCase();

  // Collapse runs of underscores and trim leading/trailing ones without an
  // anchored-alternation regex: split on `_`, drop empty segments, rejoin.
  return snaked.split("_").filter(Boolean).join("_");
}

/**
 * Map projection expressions are evaluated per item inside the activity, where
 * only `node.item` and `node.index` exist — the workflow run context does not
 * cross the activity boundary. Returns every reference in `expression` that
 * Omega's Map.CheckProjectionReferences would reject: `node.<id>` for any id
 * other than item/index, and the run-scoped `config.*`/`sys.*`/`secret.*`
 * namespaces.
 */
function findForbiddenProjectionReferences(expression: string): string[] {
  const forbidden: string[] = [];
  for (const match of expression.matchAll(/\bnode\.([\w-]+)/g)) {
    if (match[1] !== "item" && match[1] !== "index") {
      forbidden.push(`node.${match[1]}`);
    }
  }
  for (const match of expression.matchAll(/\b(config|sys|secret)\.[\w-]+/g)) {
    forbidden.push(match[0]);
  }
  return forbidden;
}

// =============================================================================
// Registry Entry Interface
// =============================================================================

/** Describes a single output field produced by a node */
interface NodeOutputField {
  /** Field name used in CEL references: node.{id}.{name} */
  name: string;
  /** Data type of the field */
  type: "string" | "number" | "boolean" | "object" | "array";
  /** If true, the field has a dynamic schema (e.g., webhook payload) */
  isSchemaless?: boolean;
}

/**
 * Describes the runtime variables a container node injects onto its
 * inner nodes by virtue of being inside the container.
 *
 * Each variable is referenced from inside the container's inner graph as:
 *   `node.<container-id>.<variable.name>`
 *
 * Example — `foreach` declares
 *   `{ name: "_each_.item", type: "object", description: "Current item" }`
 * so an inner node can write `node.fe_loop._each_.item` in CEL or
 * `{{ node.fe_loop._each_.item }}` in templates.
 *
 * Three surfaces read this field:
 *   1. AI prompt — `compactGraphRepresentation` lists the concrete
 *      ID-bound paths under each container instance in the current graph.
 *   2. AI prompt — `buildNodeCatalogForPrompt` adds an "Iteration scope
 *      variables" section to the container's catalog entry.
 *   3. Builder UI — `getAvailableVariables` injects the variables onto
 *      every inner node so they show up in the @ mention menu.
 *
 * Adding a new iteration-style container = set `isContainer: true` and
 * declare `containerScope`. No further AI-chat or builder-UI plumbing
 * required for the iteration-scope side. (Graph-op dispatch and the
 * `isContainerOmegaNode` TS guard still need the type listed there —
 * see `docs/workflow-ai-chat/README.md` for the full checklist.)
 */
export interface ContainerScope {
  variables: Array<{
    name: string;
    type: NodeOutputField["type"];
    description: string;
  }>;
}

interface NodeRegistryEntry {
  /** Display label for the node */
  label: string;
  /** FontAwesome icon name (used as a fallback when `iconUrl` is not set) */
  icon: IconName;
  /**
   * Optional path to a brand SVG under `public/`. When set, UI consumers
   * (NodePicker, on-canvas suggestion handles, node header) render this
   * image instead of the FontAwesome `icon`. Use for third-party brand
   * marks (e.g. Google Drive, Slack) where a colored logo conveys more
   * than a generic glyph.
   */
  iconUrl?: string;
  /** Short description of what the node does */
  description: string;
  /** Zod schema for the node's Omega-format parameters */
  parametersSchema?: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>;
  /**
   * Output fields this node produces, referenceable in CEL/template expressions.
   * E.g., random_number outputs `[{ name: "data", type: "number" }]`
   * so downstream nodes can use `node.{id}.data` in expressions.
   */
  outputs?: NodeOutputField[];
  /** Trigger node — no incoming edges, max 1 per workflow (e.g., start) */
  isTrigger?: boolean;
  /** Terminal node — no outgoing edges (e.g., end) */
  isTerminal?: boolean;
  /** Has onFailureEdgeId — two outgoing edges: success + failure */
  hasFailureRoute?: boolean;
  /** Branching node — multiple outgoing edges via branches (e.g., split) */
  isBranching?: boolean;
  /** Only visible to super admins in the UI */
  superAdminOnly?: boolean;
  /** Placeholder node — not user-addable (e.g., noop/ghost) */
  isPlaceholder?: boolean;
  /** Container node — holds an inner graph (e.g., sub_graph) */
  isContainer?: boolean;
  /**
   * For containers that inject runtime-scoped variables onto their
   * inner nodes (e.g. `foreach`'s `_each_.item` / `_each_.index`).
   * Read by both the AI prompt and the builder UI — see `ContainerScope`
   * above for the full integration story.
   */
  containerScope?: ContainerScope;
  /**
   * JSON example of fully populated params for the AI prompt.
   * Shown in the node catalog so the AI knows how to fill parameters.
   */
  usageExample?: string;
  /**
   * Extra context appended to this node's catalog entry in the AI prompt.
   * Use for trigger docs, usage caveats, or relationships between fields
   * that the schema alone cannot convey.
   */
  promptNotes?: string;
}

// =============================================================================
// Node Registry
// =============================================================================

export const NODE_REGISTRY: Record<string, NodeRegistryEntry> = {
  start: {
    label: "Start",
    icon: "play",
    description: "Workflow entry point",
    isTrigger: true,
    outputs: [{ name: "payload", type: "object", isSchemaless: true }],
    parametersSchema: z.object({
      schedule: z
        .object({
          cronExpression: z.string(),
          timezone: z.string().optional(),
          overlap: z.string().optional(),
        })
        .optional(),
      webhook: z
        .object({
          isEnabled: z.boolean(),
        })
        .optional(),
      inputSchema: z.record(z.unknown()).optional(),
      metadata: z
        .object({
          toolId: z.string().optional(),
          exposeAsTool: z.boolean().optional(),
          toolStartMode: z.enum(["async", "sync"]).optional(),
          acceptFiles: z.boolean().optional(),
          gailWebhookEvents: z.array(z.enum(["call_processed", "call_failed"])).optional(),
        })
        .optional(),
    }),
    usageExample: `{
  "webhook": { "isEnabled": true },
  "inputSchema": {
    "type": "object",
    "properties": {
      "claimId": { "type": "string" },
      "priority": { "type": "string" }
    },
    "required": ["claimId"]
  },
  "metadata": {
    "acceptFiles": true,
    "exposeAsTool": true,
    "toolStartMode": "sync",
    "gailWebhookEvents": ["call_processed"]
  }
}`,
    promptNotes: [
      "Trigger mechanisms: schedule (cronExpression) OR webhook (isEnabled: true).",
      "Use schedule when the workflow should run on a timer; use webhook when an external system (or Gail event) should trigger it.",
      "Set inputSchema when callers/tools should send structured input and you want required-field validation before execution.",
      "To subscribe to Gail platform events, set BOTH webhook.isEnabled AND metadata.gailWebhookEvents.",
      'Valid gailWebhookEvents: "call_processed" (call completed), "call_failed" (call failed).',
      "When gailWebhookEvents are set, the Start node outputs call payload fields (caller, assistant, call data, etc.) in addition to the generic webhook payload.",
      "acceptFiles allows attached files at workflow start; nodes can reference them via node.start.files.",
      "Enable acceptFiles ONLY when downstream nodes actually consume files (for example document_analysis or file upload/email attachment flows).",
      "Webhook URL callers must send files as multipart/form-data (not JSON data URLs).",
      "Set exposeAsTool only when this workflow should be callable by agents; keep it off for internal-only webhook/scheduled automations.",
      "toolStartMode sync is for request/response tool calls that need final output; async is for fire-and-forget/background runs.",
      "When this workflow is exposed as a tool, toolStartMode sync/async uses /workflows/start-sync or /workflows/start respectively, and files are passed through the same start API contract.",
    ].join("\n"),
  },

  end: {
    label: "End",
    icon: "stop",
    description: "Workflow end point",
    isTerminal: true,
  },

  delay: {
    label: "Delay",
    icon: "clock",
    description: "Add time delay",
    parametersSchema: z.object({
      until: omegaString,
    }),
  },

  random_number: {
    label: "Random Number",
    icon: "dice",
    description: "Generate random value",
    outputs: [{ name: "data", type: "number" }],
    parametersSchema: z.object({
      min: omegaNumber,
      max: omegaNumber,
    }),
  },

  split: {
    label: "Conditional",
    icon: "code-branch",
    description: "Branch workflow based on conditions",
    isBranching: true,
    parametersSchema: z.object({
      branches: z.array(
        z.object({
          label: z.string().optional(),
          check: omegaCel,
          edgeId: z.string(),
        })
      ),
      defaultEdgeId: z.string(),
    }),
  },

  check: {
    label: "Check",
    icon: "check",
    description: "Evaluate conditions and return an outcome label",
    outputs: [{ name: "data", type: "string" }],
    parametersSchema: z.object({
      conditions: z.array(omegaParam(z.boolean())),
      outcomes: z.array(z.string()),
      defaultOutcome: z.string(),
    }),
  },

  http_request: {
    label: "HTTP Request",
    icon: "globe",
    description: "Make an HTTP request",
    outputs: [
      { name: "status_code", type: "number" },
      { name: "response_body", type: "object", isSchemaless: true },
      { name: "response_headers", type: "object", isSchemaless: true },
    ],
    parametersSchema: z.object({
      url: omegaString,
      method: omegaString,
      headers: z.record(omegaString).optional(),
      queryParams: z.record(omegaString).optional(),
      body: omegaString.optional(),
    }),
    promptNotes: [
      "method MUST be lowercase. Allowed values: get, post, put, patch, delete.",
      "Do NOT use uppercase (GET, POST, ...) or mixed case (Get, Post, ...) - it will be coerced.",
    ].join("\n"),
    usageExample: `{
  "url": "https://api.example.com/policies/{{ node.start.payload.policyId }}",
  "method": "get",
  "headers": { "Authorization": "Bearer {{ node.start.payload.apiToken }}", "Content-Type": "application/json" }
}`,
  },

  send_email: {
    label: "Send Email",
    icon: "envelope",
    description: "Send an email message",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.error", type: "string" },
      { name: "data.status_code", type: "number" },
    ],
    parametersSchema: z.object({
      to: omegaString,
      subject: omegaString,
      body: omegaString,
      htmlContent: omegaString.optional(),
      attachmentName: omegaString.optional(),
      attachmentBase64: omegaString.optional(),
      fileAttachments: z
        .union([
          z.array(omegaParam(z.object({ storage_path: z.string(), media_type: z.string() }))),
          omegaCel,
        ])
        .nullable()
        .optional(),
    }),
    promptNotes: [
      "fileAttachments accepts an array of FilePointer references from upstream nodes (e.g. Create Text File output).",
      "Each FilePointer has storage_path and media_type fields.",
      "Use CEL references like node.<createFileNodeId>.data to reference upstream file outputs.",
    ].join("\n"),
    usageExample: `{
  "to": "agent@insurance.com",
  "subject": "Action Required: Missing Documents for Policy {{ node.docAnalysis.data.summary }}",
  "body": "Hello, The following documents are missing or incomplete: {{ node.agent.data.output.missingDocuments }}. Please upload the required documents within 72 hours. Thank you.",
  "fileAttachments": [{ "type": "cel", "value": "node.createFile123.data" }]
}`,
  },

  send_sms: {
    label: "Send SMS",
    icon: "message" as IconName,
    description: "Send an SMS text message",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      to: omegaString,
      message: omegaString,
    }),
    usageExample: `{
  "to": "+15551234567",
  "message": "Hi {{ node.start.payload.first_name }}, your policy renewal is due. Reply STOP to opt out."
}`,
    promptNotes: [
      "to must be a phone number in E.164 format (e.g. +15551234567) or a CEL/template reference resolving to one.",
      "message is the freeform text body of the SMS.",
      "On failure, data carries Gail problem details (type, title, status, detail, instance).",
    ].join("\n"),
  },

  create_file: {
    label: "Create Text File",
    icon: "file-circle-plus" as IconName,
    description: "Create a file from text or binary content",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.id", type: "string" },
      { name: "data.file_name", type: "string" },
      { name: "data.media_type", type: "string" },
      { name: "data.size_bytes", type: "number" },
      { name: "data.storage_path", type: "string" },
    ],
    parametersSchema: z.object({
      content: omegaString,
      fileName: omegaString,
      mediaType: omegaString,
    }),
    usageExample: `{
  "content": "Name,Email,Status\\nJohn,john@example.com,Active",
  "fileName": "report.csv",
  "mediaType": "text/csv"
}`,
    promptNotes: [
      "Content is UTF-8 text for text MIME types (text/*, application/json, etc.).",
      "For non-text MIME types (image/png, application/pdf, etc.), content must be base64-encoded.",
      "Create Text File is NOT for filling out existing PDF forms. If the user asks to fill/form-fill a PDF, do NOT use create_file.",
      "FileName must include an extension and cannot contain path separators (/ or \\).",
      "MediaType must be a valid MIME type in type/subtype format.",
      "On success, data contains a FileReference with id, fileName, mediaType, sizeBytes, and storagePath.",
      "The output FileReference can be used by downstream nodes like SFTP Upload or Send Email attachments.",
    ].join("\n"),
  },

  parse_csv: {
    label: "Parse CSV",
    icon: "file-csv" as IconName,
    description: "Parse a CSV file into rows for downstream nodes",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.rows", type: "array" },
      { name: "data.column_names", type: "array" },
      { name: "data.row_count", type: "number" },
      { name: "data.error", type: "string" },
    ],
    parametersSchema: z
      .object({
        file: omegaFilePointer,
        hasHeader: omegaParam(z.boolean().default(true)),
        columns: z.union([z.array(omegaString), omegaCel]).optional(),
        skipRows: omegaParam(z.number().int().min(0)).optional(),
        maxRows: omegaParam(z.number().int().min(0).max(10_000)).optional(),
        filter: omegaCel.optional(),
      })
      .superRefine((value, ctx) => {
        if (
          value.hasHeader.type === "literal" &&
          value.hasHeader.value === false &&
          !value.columns
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["columns"],
            message: "'Columns' is required when 'Has header' is false.",
          });
        }
      }),
    usageExample: `{
  "file": { "type": "cel", "value": "node.sftp_download.data.file" },
  "hasHeader": { "type": "literal", "value": true },
  "maxRows": { "type": "literal", "value": 500 },
  "filter": { "type": "cel", "value": "node.item.status == 'Active'" }
}`,
    promptNotes: [
      "file must resolve to a FilePointer from an upstream node (Create Text File, SFTP/Drive download, etc.).",
      "When hasHeader is false, columns is REQUIRED — provide a positional name for each CSV column.",
      "Column names are normalized to snake_case for CEL access. Reference cells in filter as node.item.<column_snake_case>.",
      "columns (when provided with hasHeader=true) acts as a whitelist that also fixes output column order.",
      "filter is a CEL boolean expression evaluated per row (e.g. node.item.status == 'Active'); rows that evaluate false are dropped.",
      "skipRows applies BEFORE filter; maxRows caps the post-filter result.",
      "Hard caps: maxRows ≤ 10,000, columns ≤ 1,000, cells ≤ 1,000,000 characters. Use filter/maxRows/skipRows to stay under the limits.",
      "On success, data.rows is an array of objects keyed by normalized column name. data.columnNames preserves the output column order. data.rowCount is the post-filter count.",
    ].join("\n"),
  },

  slice_csv: {
    label: "Slice CSV",
    icon: "file-csv" as IconName,
    description: "Split a CSV file into smaller row-capped slices",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.slices", type: "array" },
      { name: "data.slice_count", type: "number" },
      { name: "data.total_rows", type: "number" },
      { name: "data.error", type: "string" },
    ],
    parametersSchema: z.object({
      file: omegaFilePointer,
      hasHeader: omegaParam(z.boolean().default(true)),
      maxRows: omegaParam(z.number().int().min(0)).optional(),
    }),
    usageExample: `{
  "file": { "type": "cel", "value": "node.sftp_download.data.file" },
  "hasHeader": { "type": "literal", "value": true },
  "maxRows": { "type": "literal", "value": 500 }
}`,
    promptNotes: [
      "file must resolve to a FilePointer from an upstream node (Create Text File, SFTP/Drive download, etc.).",
      "hasHeader controls whether the first row is treated as a header; when true, the header row is repeated at the top of every slice.",
      "maxRows caps the number of data rows per slice. When omitted, the file is not split and a single slice is produced.",
      "On success, data.slices is an array of FilePointers (one per slice) usable by downstream nodes. data.slice_count is the number of slices and data.total_rows is the total data row count across all slices.",
    ].join("\n"),
  },

  create_csv: {
    label: "Create CSV",
    icon: "file-csv" as IconName,
    description: "Build a CSV file from rows produced by upstream nodes",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.id", type: "string" },
      { name: "data.file_name", type: "string" },
      { name: "data.media_type", type: "string" },
      { name: "data.size_bytes", type: "number" },
      { name: "data.storage_path", type: "string" },
      { name: "data.error", type: "string" },
    ],
    parametersSchema: z
      .object({
        rows: omegaParam(z.array(z.unknown())),
        fileName: omegaString,
        includeHeader: omegaParam(z.boolean()).optional(),
        fields: z.union([z.array(omegaString), omegaCel]).optional(),
        headers: z.union([z.array(omegaString), omegaCel]).optional(),
        skipRows: omegaParam(z.number().int().min(0)).optional(),
        maxRows: omegaParam(z.number().int().min(0).max(10_000)).optional(),
        filter: omegaCel.optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's ValidateFieldsHeadersLengthMatch — only when both
        // are literal arrays, since CEL refs resolve at runtime.
        if (
          Array.isArray(value.fields) &&
          Array.isArray(value.headers) &&
          value.fields.length !== value.headers.length
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["headers"],
            message:
              "'Fields' and 'Headers' must have the same length when both are provided.",
          });
        }
      }),
    usageExample: `{
  "rows": { "type": "cel", "value": "node.parse_csv.data.rows" },
  "fileName": { "type": "literal", "value": "active-leads.csv" },
  "includeHeader": { "type": "literal", "value": true },
  "fields": [
    { "type": "literal", "value": "first_name" },
    { "type": "literal", "value": "email" }
  ],
  "headers": [
    { "type": "literal", "value": "First Name" },
    { "type": "literal", "value": "Email" }
  ]
}`,
    promptNotes: [
      "rows must be a CEL expression that resolves to an array of objects (each row keyed by column name).",
      String.raw`fileName must include a .csv extension and cannot contain path separators (/ or \).`,
      "When fields is omitted, the output uses every key found across the input rows in first-seen order.",
      "When both fields and headers are provided, they MUST have the same length — fields selects/orders source keys, headers becomes the header row.",
      "filter is a CEL boolean expression evaluated per row against node.item.<key>; rows that evaluate false are dropped.",
      "skipRows applies BEFORE filter; maxRows caps the post-filter result.",
      "Hard caps: maxRows ≤ 10,000, columns ≤ 1,000, cells ≤ 1,000,000 characters, output file size capped by the workflow file limit.",
      "On success, data is a FileReference (id, fileName, mediaType, sizeBytes, storagePath) usable by Send Email attachments, SFTP Upload, Google Drive Upload, etc.",
    ].join("\n"),
  },

  start_outbound_call: {
    label: "Outbound Call",
    icon: "phone",
    description: "Make an outbound call",
    parametersSchema: z.object({
      to: omegaString,
      script: omegaParam(
        z.object({
          name: z.string(),
          greeting: z.string(),
          greetingMode: z.enum(["static", "prompt", "random", "time_specific"]).optional(),
          greetingPrompt: z.string().optional(),
          greetingVariants: z.array(z.string()).optional(),
          greetingMorning: z.string().optional(),
          greetingAfternoon: z.string().optional(),
          greetingNight: z.string().optional(),
          greetingMorningStart: z.string().optional(),
          greetingAfternoonStart: z.string().optional(),
          greetingNightStart: z.string().optional(),
          objective: z.string(),
          policy: z.string(),
          voicemail: z.string(),
          goodbye: z.string(),
        })
      ),
    }),
  },

  pipe: {
    label: "Data Transform",
    icon: "shuffle",
    description: "Transform data into variables",
    parametersSchema: z.object({
      data: z.record(omegaParam(z.unknown())),
    }),
  },

  add_contact: {
    label: "Add Contact",
    icon: "user-plus",
    description: "Add a new contact to the system",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.id", type: "string" },
      { name: "data.status", type: "string" },
      { name: "data.first_name", type: "string" },
      { name: "data.last_name", type: "string" },
      { name: "data.emails", type: "array" },
      { name: "data.phone_numbers", type: "array" },
      { name: "data.business_name", type: "string" },
      { name: "data.additional_data", type: "object" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      firstName: omegaString,
      lastName: omegaString,
      emails: z.array(omegaString),
      phoneNumbers: z.array(omegaParam(z.object({ number: z.string(), type: z.string() }))),
      businessName: omegaString,
      additionalData: z.record(omegaParam(z.unknown())),
    }),
  },

  add_bulk_contacts: {
    label: "Add Bulk Contacts",
    icon: "users" as IconName,
    description:
      "Bulk-create Gail contacts from a list of rows (CSV-friendly, success/failure branches)",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.succeeded", type: "number" },
      { name: "data.failed", type: "number" },
      { name: "data.successful_contact_ids", type: "array" },
      { name: "data.failures", type: "array" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      // `contacts` is Omega's ValueList<ContactItem>. The array form is the
      // schema default; the whole-list CEL reference form is also valid (see
      // promptNotes) and handled by the transformer.
      contacts: z.array(
        omegaParam(
          z.object({
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
            phone: z.string(),
            phoneType: z.string().optional(),
            businessName: z.string(),
            additionalData: z.record(z.unknown()).optional(),
          })
        )
      ),
      dedupe: omegaParam(z.boolean()).optional(),
    }),
    usageExample: `{
  "contacts": { "type": "cel", "value": "node.parse_csv_id.data.rows" },
  "dedupe": { "type": "literal", "value": true }
}`,
    promptNotes: [
      "Bulk-creates many contacts in one call. Best paired with a Parse CSV node.",
      "contacts accepts EITHER a single whole-list CEL reference (the common case, e.g. node.parse_csv_id.data.rows) OR a literal array of contact rows.",
      "Each contact row is flat: firstName (required), lastName, email (required, valid), phone (required, valid), phoneType (Mobile|Home|Work|Fax, defaults Mobile), businessName, optional additionalData.",
      "dedupe defaults to true — the Gail API skips contacts matching existing ones (safe under retry).",
      "Per-row failures do NOT take the failure route; only a transport-level error (4xx) does. Inspect data.failures for rejected rows.",
    ].join("\n"),
  },

  create_contact_list: {
    label: "Create Contact List",
    icon: "list" as IconName,
    description: "Create a new contact list in Gail",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.id", type: "string" },
      { name: "data.list_name", type: "string" },
      { name: "data.description", type: "string" },
      { name: "data.status", type: "string" },
      { name: "data.contact_count", type: "number" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      listName: omegaString,
      description: omegaString,
    }),
    usageExample: `{
  "listName": "New policy leads",
  "description": "Contacts collected from the latest lead import"
}`,
    promptNotes: "Both listName and description are required.",
  },

  start_campaign: {
    label: "Start Campaign",
    icon: "play",
    description: "Start a campaign (success/failure branches)",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.id", type: "string" },
      { name: "data.name", type: "string" },
      { name: "data.description", type: "string" },
      { name: "data.status", type: "string" },
      { name: "data.sequences", type: "array" },
      { name: "data.redialing_rules", type: "string" },
      { name: "data.contact_lists", type: "array" },
      { name: "data.created_at", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
    ],
    parametersSchema: z.object({
      campaignId: omegaUuid,
    }),
  },

  create_campaign: {
    label: "Create Campaign",
    icon: "bullhorn",
    description: "Clone a template campaign (success/failure branches)",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.campaign_id", type: "string" },
      { name: "data.template_campaign_id", type: "string" },
      { name: "data.campaign_name", type: "string" },
      { name: "data.status", type: "string" },
      { name: "data.sequence_count", type: "number" },
      { name: "data.contact_list_count", type: "number" },
      { name: "data.has_redialing_rules", type: "boolean" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
      { name: "data.type", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      templateCampaignId: omegaUuid,
      name: omegaString.optional(),
      status: omegaString.optional(),
    }),
    usageExample: `{
  "templateCampaignId": { "type": "literal", "value": "018f0000-0000-7000-8000-000000000001" },
  "name": { "type": "literal", "value": "Q3 Renewal Outreach" },
  "status": { "type": "literal", "value": "Draft" }
}`,
    promptNotes: [
      "templateCampaignId is the existing campaign to clone (sequences, contact lists, and redialing rules are copied).",
      "name is optional — when omitted, the new campaign keeps the template's name.",
      "status is optional and must be one of: Draft, Active, Stopped. When omitted, the template's status is used.",
    ].join("\n"),
  },

  add_contact_to_campaign: {
    label: "Add Contact to Campaign",
    icon: "bullhorn",
    description: "Add contact to a campaign",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.campaign_id", type: "string" },
      { name: "data.contact_id", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
    ],
    parametersSchema: z.object({
      campaignId: omegaUuid,
      // Default to empty CEL so the settings panel opens in variable mode
      // instead of showing a naked empty GUID in static text.
      contactId: omegaUuid.default({ type: "cel", value: "" }),
    }),
  },

  add_contacts_to_campaign_bulk: {
    label: "Add Contacts to Campaign (Bulk)",
    icon: "bullhorn",
    description: "Add one or more existing contacts to a campaign in bulk",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.campaign_id", type: "string" },
      { name: "data.requested_count", type: "number" },
      { name: "data.failures", type: "array" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      campaignId: omegaUuid,
      contactIds: z.union([z.array(omegaUuid).min(1).max(1000), omegaCel]),
    }),
    usageExample: `{
  "campaignId": { "type": "literal", "value": "018f0000-0000-7000-8000-000000000001" },
  "contactIds": [{ "type": "cel", "value": "node.add_contact.id" }]
}`,
    promptNotes: [
      "contactIds accepts either a list of individual contact IDs (each a literal UUID or a per-item CEL reference, max 1000 per call) OR a single CEL expression that resolves to the whole Guid[] (e.g. an upstream node output array).",
      "On success, data.requested_count reports how many contacts were submitted and data.failures lists any contacts that could not be added (partial success).",
      "The node supports success and failure routes.",
    ].join("\n"),
  },

  add_contact_to_contact_list: {
    label: "Add Contact to Contact List",
    icon: "address-book" as IconName,
    description: "Add one or more contacts to a contact list",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.contact_list_id", type: "string" },
      { name: "data.contact_ids", type: "array" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      contactListId: omegaUuid,
      contactIds: z.union([z.array(omegaUuid).min(1).max(2500), omegaCel]),
    }),
    usageExample: `{
  "contactListId": { "type": "literal", "value": "018f0000-0000-7000-8000-000000000001" },
  "contactIds": [{ "type": "cel", "value": "node.add_contact.id" }]
}`,
    promptNotes: [
      "contactIds accepts either a list of individual contact IDs (each a literal UUID or a per-item CEL reference, max 2500 per call) OR a single CEL expression that resolves to the whole Guid[] (e.g. an upstream node output array).",
      "The node supports success and failure routes.",
    ].join("\n"),
  },

  get_call_data: {
    label: "Get Call Data",
    icon: "phone-arrow-down-left" as IconName,
    description: "Retrieve call data by SID from Gail",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.sid", type: "string" },
      { name: "data.name", type: "string" },
      { name: "data.category_of_call", type: "string" },
      { name: "data.reason_of_call", type: "string" },
      { name: "data.call_disconnect_reason", type: "string" },
      { name: "data.voicemail", type: "string" },
      { name: "data.recording", type: "object" },
      { name: "data.transcript", type: "array" },
      { name: "data.extended_call_information", type: "object" },
      { name: "data.custom_fields", type: "object" },
      { name: "data.detail", type: "string" },
    ],
    parametersSchema: z.object({
      callSid: omegaString,
    }),
    promptNotes: [
      "CallSid should be a Twilio Call SID (typically CA followed by 32 hex characters) but can also be a CEL expression.",
      "On success, data contains call metadata including recording, transcript, caller information, and custom fields.",
      "The recording output is an object. The transcript output is an array of entries. The extended_call_information and custom_fields outputs are objects.",
    ].join("\n"),
  },

  download_call_recording: {
    label: "Download Call Recording",
    icon: "arrow-down-to-square" as IconName,
    description: "Download a Gail call recording into workflow file storage",
    hasFailureRoute: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.file", type: "object" },
      { name: "data.file.id", type: "string" },
      { name: "data.file.file_name", type: "string" },
      { name: "data.file.media_type", type: "string" },
      { name: "data.file.size_bytes", type: "number" },
      { name: "data.file.storage_path", type: "string" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
    ],
    parametersSchema: z.object({
      callSid: omegaString,
    }),
    promptNotes: [
      "CallSid should be a Twilio Call SID (typically CA followed by 32 hex characters) but can also be a CEL expression.",
      "On success, data.file is a FileReference (id, fileName, mediaType, sizeBytes, storagePath) that downstream nodes can attach or upload (e.g. Send Email, SFTP Upload).",
      "On failure, data carries Gail problem details (type, title, status, detail, instance).",
    ].join("\n"),
  },

  slack_direct_message: {
    label: "Slack DM",
    icon: "comment",
    description: "Send a Slack direct message",
    parametersSchema: z.object({
      recipient: omegaString,
      message: omegaString,
      botToken: omegaSecret,
    }),
  },

  slack_post_channel_message: {
    label: "Slack Channel Message",
    icon: "comment",
    description: "Post a message to a Slack channel",
    parametersSchema: z.object({
      channel: omegaString,
      message: omegaString,
      botToken: omegaSecret,
    }),
    usageExample: `{
  "channel": "#underwriting-review",
  "message": "New file needs manual review. Decision: {{ node.agent.data.output.decision }}. Reason: {{ node.agent.data.output.summary }}",
  "botToken": { "type": "cel", "value": "secret.slack_bot_token" }
}`,
  },

  chaos: {
    label: "Chaos",
    icon: "bolt",
    description: "Random failure for testing",
    hasFailureRoute: true,
    superAdminOnly: true,
    outputs: [
      { name: "is_success", type: "boolean" },
      { name: "data.chance_to_fail", type: "number" },
      { name: "data.value_chosen", type: "number" },
      { name: "data.reason", type: "string" },
    ],
    parametersSchema: z.object({
      chanceToFail: omegaNumber,
    }),
  },

  web_search: {
    label: "Web Search",
    icon: "magnifying-glass" as IconName,
    description: "Search the web and retrieve results",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.results", type: "array" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      query: omegaString,
      limit: omegaParam(z.number().int().min(1).max(10)).optional(),
      contentFormat: omegaParam(z.enum(CONTENT_FORMATS)).optional(),
    }),
    usageExample: `{
  "query": "latest AI research papers 2026",
  "limit": 5,
  "contentFormat": "markdown"
}`,
    promptNotes:
      "Searches the web via Firecrawl and returns a list of WebResource results. Each result has url, title, description, and content fields. contentFormat options: markdown (default), summary, links, html, raw_html. Commonly composed with an Agent node to extract structured data from search results.",
  },

  web_extract: {
    label: "Web Extract",
    icon: "magnifying-glass-arrow-right" as IconName,
    description: "Extract structured data from web pages",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.results", type: "object", isSchemaless: true },
      ...failureErrorOutputs,
    ],
    parametersSchema: z
      .object({
        // Backend requires every nullable WebResource field to be present with
        // an explicit null (RespectRequiredConstructorParameters=true), so the
        // schema uses nullable() rather than optional() to reflect the wire shape.
        sources: z.union([
          z
            .array(
              omegaParam(
                z.object({
                  url: z.string(),
                  title: z.string().nullable(),
                  description: z.string().nullable(),
                  content: z.string().nullable(),
                })
              )
            )
            .min(1)
            .max(10),
          omegaCel,
        ]),
        prompt: omegaString.optional(),
        schema: omegaString.optional(),
      })
      .superRefine((value, ctx) => {
        const isEmptyOmegaString = (fieldValue: unknown) => {
          if (fieldValue == null) return true;
          if (typeof fieldValue === "string") return fieldValue.trim().length === 0;
          if (
            typeof fieldValue === "object" &&
            "value" in fieldValue &&
            typeof (fieldValue as { value: unknown }).value === "string"
          ) {
            return (fieldValue as { value: string }).value.trim().length === 0;
          }
          return false;
        };
        if (isEmptyOmegaString(value.prompt) && isEmptyOmegaString(value.schema)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["prompt"],
            message: "Either prompt or schema is required.",
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["schema"],
            message: "Either prompt or schema is required.",
          });
        }
      }),
    usageExample: `{
  "sources": { "type": "cel", "value": "node.search_id.data.results" },
  "prompt": "Extract the business name, FEIN, status, registered agent, and formation date",
  "schema": "{\\"type\\":\\"object\\",\\"properties\\":{\\"business_name\\":{\\"type\\":\\"string\\"},\\"fein\\":{\\"type\\":\\"string\\"}}}"
}`,
    promptNotes: [
      "Extracts structured data from web pages via Firecrawl. No Omega-side LLM call — Firecrawl's model reads each page and returns a dictionary of fields.",
      "sources may be either an array with 1-10 entries or a single top-level CEL reference to an upstream WebSearch result list (e.g. node.search_id.data.results). Only the url field is used by the backend.",
      "At least one of prompt or schema is required. Combining them (natural-language intent + JSON Schema structure) is the recommended and most reliable form.",
      "schema, if provided as a literal, must be a valid JSON string (JSON Schema correctness is checked by Firecrawl at runtime).",
      "Output fields depend on the prompt/schema — reference them via node.<id>.data.results.<field_name>.",
      "Multi-URL merge uses first-write-wins with priority ordering: the top-ranked source wins, later sources fill gaps.",
    ].join("\n"),
  },

  agent: {
    label: "Agent",
    icon: "robot" as IconName,
    description: "Use AI to process data and return structured output",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.output", type: "object", isSchemaless: true },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      prompt: omegaString,
      // ToolsObjectSchema (raw on UI side; transformer wraps it in literal for the wire).
      desiredOutputFields: z.object({
        type: z.literal("object").default("object"),
        description: z.string().default(""),
        properties: z.record(z.unknown()).default({}),
        required: z.array(z.string()).default([]),
      }),
      agentInput: omegaString.optional(),
    }),
    usageExample: `{
  "prompt": "You are an insurance underwriting assistant. Analyze the extracted document data and evaluate against the following rules: 1) All required signatures must be present 2) Driver license count must match driver count 3) VINs must match between application and registration. Return a decision of COMPLETE, INCOMPLETE, or NEEDS_REVIEW with detailed reasoning.",
  "desiredOutputFields": {
    "decision": "COMPLETE | INCOMPLETE | NEEDS_REVIEW",
    "missing_documents": ["list of missing document names"],
    "failed_rules": ["list of rules that failed with reasons"],
    "summary": "1-2 sentence overall assessment"
  },
  "agentInput": "node.docAnalysis.data"
}`,
  },

  run_skill: {
    label: "Run Skill",
    icon: "wand-magic-sparkles" as IconName,
    description: "Execute a cataloged Agent Skill in an isolated sandbox",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.generated_files", type: "array" },
      { name: "data.final_text", type: "string" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      skillId: omegaLiteralUuid,
      instruction: omegaString,
      files: z.array(omegaCel).optional(),
    }),
    usageExample: `{
  "skillId": { "type": "literal", "value": "00000000-0000-0000-0000-000000000001" },
  "instruction": "Read the uploaded file and produce a short summary of what you did.",
  "files": [
    { "type": "cel", "value": "node.start_id.files[0]" }
  ]
}`,
    promptNotes:
      "skillId must be a literal GUID (not CEL). Use the organization's Skills catalog. files is optional — each entry is a CEL expression resolving to a single FilePointer (e.g. node.start_id.files[0]).",
  },

  document_analysis: {
    label: "Document Analysis",
    icon: "file-lines" as IconName,
    description: "Analyze uploaded files to identify document types and extract data",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.documents", type: "array" },
      { name: "data.summary", type: "string" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      files: omegaCel,
      targetItems: z.array(
        z.object({
          documentType: z.string(),
          description: z.string(),
          fields: z
            .array(
              z.object({
                fieldName: z.string(),
                description: z.string(),
              })
            )
            .optional(),
        })
      ),
    }),
    usageExample: `{
  "files": "node.start.files",
  "targetItems": [
    {
      "documentType": "Signed Application",
      "description": "Primary insurance application signed by the insured with date stamp",
      "fields": [
        { "fieldName": "applicantName", "description": "Full name of the applicant" },
        { "fieldName": "signaturePresent", "description": "Whether a signature is detected (true/false)" },
        { "fieldName": "signatureDate", "description": "Date on the signature" }
      ]
    },
    {
      "documentType": "Driver License",
      "description": "Photo ID for identity verification of rated drivers",
      "fields": [
        { "fieldName": "licenseNumber", "description": "Driver license number" },
        { "fieldName": "fullName", "description": "Name on the license" },
        { "fieldName": "address", "description": "Address on the license" },
        { "fieldName": "expirationDate", "description": "License expiration date" }
      ]
    },
    {
      "documentType": "Vehicle Registration",
      "description": "Registration confirming VIN and ownership",
      "fields": [
        { "fieldName": "vin", "description": "Vehicle Identification Number" },
        { "fieldName": "ownerName", "description": "Registered owner name" },
        { "fieldName": "registrationAddress", "description": "Address on registration" }
      ]
    },
    {
      "documentType": "Payment Receipt",
      "description": "Proof of premium payment",
      "fields": [
        { "fieldName": "amount", "description": "Payment amount" },
        { "fieldName": "paymentDate", "description": "Date of payment" },
        { "fieldName": "paymentMethod", "description": "Method of payment (check, card, etc.)" }
      ]
    }
  ]
}`,
    promptNotes:
      "files must be a CEL expression that resolves to the input files array (for example: node.start.files). Do not use fileCount.",
  },

  jotform_submit_form: {
    label: "JotForm Submit",
    icon: "file-signature" as IconName,
    description: "Submit answers to a JotForm form",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.submission_id", type: "string" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      formId: omegaString,
      apiKey: omegaSecret,
      // KeyValuePairWithExpression[] — UI keeps key/value pairs ordered;
      // transformer converts to a record on the wire.
      answers: z.array(z.object({ key: z.string(), value: omegaString })),
    }),
    usageExample: `{
  "formId": { "type": "literal", "value": "260056698857069" },
  "answers": {
    "First Name": { "type": "cel", "value": "node.start_id.payload.first_name" },
    "Last Name": { "type": "cel", "value": "node.start_id.payload.last_name" },
    "Email": { "type": "cel", "value": "node.start_id.payload.email" }
  },
  "apiKey": { "type": "cel", "value": "secret.jotform_api_key" }
}`,
    promptNotes: [
      'Answer keys are human-readable question labels (e.g., "First Name", "Email"), not JotForm question IDs.',
      "Label matching is case-insensitive at runtime.",
      'Multi-part fields (e.g., Full Name) use sublabel keys like "First Name" and "Last Name" individually.',
      "apiKey is a secret: it must reference secret.* (e.g. secret.jotform_api_key), never a plaintext literal.",
      "On failure, data.error is one of: questions_fetch_failed, duplicate_labels, unrecognized_labels, submit HTTP {status}.",
    ].join("\n"),
  },

  sftp_upload_file: {
    label: "SFTP Upload",
    icon: "server" as IconName,
    description: "Upload a file to an SFTP server",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.remote_path", type: "string" },
      { name: "data.bytes_transferred", type: "number" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      ...sftpConnectionSchema,
      file: omegaFilePointer,
    }),
    usageExample: `{
  "host": "sftp.example.com",
  "port": { "type": "literal", "value": 22 },
  "username": "deploy",
  "password": { "type": "cel", "value": "secret.sftp_password" },
  "remotePath": "/uploads/report.csv",
  "file": { "type": "cel", "value": "node.docAnalysis.data.documents[0].file" }
}`,
    promptNotes: [
      "Requires at least one of 'password' or 'privateKey' for authentication.",
      "password and privateKey are secrets: each must reference secret.* (e.g. secret.sftp_password), never a plaintext literal.",
      "Port must be between 1 and 65535 (defaults to 22).",
      "The 'file' parameter references a FilePointer from a previous node output.",
    ].join("\n"),
  },

  sftp_download_file: {
    label: "SFTP Download",
    icon: "server" as IconName,
    description: "Download a file from an SFTP server",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.file", type: "object" },
      { name: "data.file.id", type: "string" },
      { name: "data.file.file_name", type: "string" },
      { name: "data.file.media_type", type: "string" },
      { name: "data.file.size_bytes", type: "number" },
      { name: "data.file.storage_path", type: "string" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      ...sftpConnectionSchema,
    }),
    usageExample: `{
  "host": "sftp.example.com",
  "port": { "type": "literal", "value": 22 },
  "username": "deploy",
  "password": { "type": "cel", "value": "secret.sftp_password" },
  "remotePath": "/reports/daily.csv"
}`,
    promptNotes: [
      "Requires at least one of 'password' or 'privateKey' for authentication.",
      "password and privateKey are secrets: each must reference secret.* (e.g. secret.sftp_password), never a plaintext literal.",
      "Port must be between 1 and 65535 (defaults to 22).",
      "The remote file extension must be in the allowed file types list.",
      "On success, data.file is a FileReference that can be used by downstream nodes.",
    ].join("\n"),
  },

  sftp_list_directory: {
    label: "SFTP List Directory",
    icon: "server" as IconName,
    description: "List files in an SFTP directory",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.entries", type: "array" },
      { name: "data.total_matched", type: "number" },
      { name: "data.count", type: "number" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      ...sftpConnectionSchema,
      namePattern: omegaString.optional(),
      modifiedAfter: omegaString.optional(),
      modifiedBefore: omegaString.optional(),
      includeDirectories: omegaParam(z.boolean()).optional(),
      sortBy: omegaString.optional(),
      sortDescending: omegaParam(z.boolean()).optional(),
      maxResults: omegaNumber.optional(),
    }),
    usageExample: `{
  "host": "sftp.example.com",
  "port": { "type": "literal", "value": 22 },
  "username": "deploy",
  "password": { "type": "cel", "value": "secret.sftp_password" },
  "remotePath": "/reports/",
  "namePattern": "*.csv",
  "sortBy": { "type": "literal", "value": "modified" },
  "sortDescending": { "type": "literal", "value": true },
  "maxResults": { "type": "literal", "value": 10 }
}`,
    promptNotes: [
      "Requires at least one of 'password' or 'privateKey' for authentication.",
      "password and privateKey are secrets: each must reference secret.* (e.g. secret.sftp_password), never a plaintext literal.",
      "Port must be between 1 and 65535 (defaults to 22).",
      "namePattern uses glob-style matching (e.g., '*.csv').",
      "modifiedAfter/modifiedBefore must be ISO 8601 date-time strings.",
      "sortBy must be one of: 'name', 'modified', 'size'.",
      "Each entry in data.entries has: name, fullPath, sizeBytes, isDirectory, lastModifiedUtc.",
    ].join("\n"),
  },

  google_drive_list_files: {
    label: "Google Drive List Files",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "List files and folders from Google Drive",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.entries", type: "array" },
      { name: "data.count", type: "number" },
      { name: "data.truncated", type: "boolean" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      folderId: omegaString.optional(),
      namePattern: omegaString.optional(),
      mimeType: omegaString.optional(),
      modifiedAfter: omegaString.optional(),
      modifiedBefore: omegaString.optional(),
      includeFolders: omegaParam(z.boolean()).optional(),
      includeTrashed: omegaParam(z.boolean()).optional(),
      orderBy: omegaString.optional(),
      orderDescending: omegaParam(z.boolean()).optional(),
      maxResults: omegaParam(z.number().int().min(1).max(1000)).optional(),
    }),
    usageExample: `{
  "folderId": { "type": "literal", "value": "1AbCdEfGhIjKlMnOpQrStUv" },
  "mimeType": { "type": "literal", "value": "text/csv" },
  "orderBy": { "type": "literal", "value": "modifiedTime" },
  "orderDescending": { "type": "literal", "value": true },
  "maxResults": { "type": "literal", "value": 100 }
}`,
    promptNotes: [
      "Requires an active Google credential connected to the organization.",
      "orderBy values supported by Omega: name, modifiedTime, createdTime, quotaBytesUsed.",
      "When Drive has additional pages, data.truncated is true so workflows can branch.",
    ].join("\n"),
  },

  google_drive_download_file: {
    label: "Google Drive Download File",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Download a file from Google Drive into workflow storage",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.file", type: "object" },
      { name: "data.file.id", type: "string" },
      { name: "data.file.file_name", type: "string" },
      { name: "data.file.media_type", type: "string" },
      { name: "data.file.size_bytes", type: "number" },
      { name: "data.file.storage_path", type: "string" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      fileId: omegaString,
      exportMediaType: omegaString.optional(),
    }),
    usageExample: `{
  "fileId": { "type": "literal", "value": "1AbCdEfGhIjKlMnOpQrStUv" },
  "exportMediaType": { "type": "literal", "value": "application/pdf" }
}`,
    promptNotes: [
      "Requires an active Google credential connected to the organization.",
      "Use exportMediaType for Google Workspace native files (Docs/Sheets/Slides/Drawings).",
      "On success, data.file can be passed to other nodes that accept FilePointer values.",
    ].join("\n"),
  },

  google_drive_upload_file: {
    label: "Google Drive Upload File",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Upload a workflow file into Google Drive",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.file_id", type: "string" },
      { name: "data.name", type: "string" },
      { name: "data.bytes_transferred", type: "number" },
      { name: "data.web_view_link", type: "string" },
      { name: "data.was_created", type: "boolean" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      file: omegaFilePointer,
      fileName: omegaString,
      folderId: omegaString.optional(),
      mediaType: omegaString.optional(),
    }),
    usageExample: `{
  "file": { "type": "cel", "value": "node.gdrive_dl_id.data.file" },
  "fileName": { "type": "literal", "value": "monthly-report.csv" },
  "folderId": { "type": "literal", "value": "1AbCdEfGhIjKlMnOpQrStUv" }
}`,
    promptNotes: [
      "Requires an active Google credential connected to the organization.",
      "file should resolve to a FilePointer from an upstream node.",
      "Upload is upsert-style by folderId + fileName; data.wasCreated indicates insert vs update.",
    ].join("\n"),
  },

  google_sheets_append_rows: {
    label: "Google Sheets Append Rows",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Append rows to a Google Sheet by matching column headers",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.updated_rows", type: "number" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z.object({
      spreadsheetId: omegaString,
      rows: z.union([
        omegaLiteral(z.array(z.record(z.unknown()))),
        omegaCel,
      ]),
      sheetName: omegaString.optional(),
    }),
    usageExample: `{
  "spreadsheetId": { "type": "literal", "value": "1AbCdEfGhIjKlMnOpQrStUv" },
  "sheetName": { "type": "literal", "value": "Leads" },
  "rows": { "type": "cel", "value": "node.parse_csv_id.data.rows" }
}`,
    promptNotes: [
      "Requires an active Google credential connected to the organization.",
      "spreadsheetId is the value between /d/ and /edit in the Google Sheets URL, not the full URL.",
      "sheetName is the tab title, not its numeric gid. Omit it to use the first visible sheet.",
      "rows must resolve to an array of objects. Object keys are matched case-insensitively to row 1 headers.",
      "Extra object keys are ignored and missing keys produce blank cells.",
    ].join("\n"),
  },

  agencyzoom_search_customers: {
    label: "AgencyZoom Search Customers",
    icon: "users" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom customers with optional filters",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.total_count", type: "number" },
      { name: "data.page", type: "number" },
      { name: "data.page_size", type: "number" },
      { name: "data.customers", type: "array", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(100)).optional(),
      sort: omegaString.optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      firstname: omegaString.optional(),
      lastname: omegaString.optional(),
      email: omegaString.optional(),
      phone: omegaString.optional(),
      state: omegaString.optional(),
      city: omegaString.optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
      leadSourceId: omegaParam(z.number().int()).optional(),
      agentId: omegaParam(z.number().int()).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_CUSTOMER_STATUS_VALUES)).optional(),
    }),
    usageExample: `{
  "pageSize": { "type": "literal", "value": 25 },
  "order": { "type": "literal", "value": "desc" },
  "firstname": { "type": "literal", "value": "Jane" },
  "status": { "type": "literal", "value": "active" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "All search filters are optional; omit unset filters rather than sending empty values.",
      "pageSize must be between 1 and 100. order must be asc or desc.",
      "status must be one of: all, inactive, active.",
      "startDate and endDate must be valid ISO date-time strings.",
    ].join("\n"),
  },

  agencyzoom_get_customer: {
    label: "AgencyZoom Get Customer",
    icon: "user" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Fetch a single AgencyZoom customer by ID",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data", type: "object", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      customerId: omegaParam(z.number().int().positive()),
    }),
    usageExample: `{
  "customerId": { "type": "cel", "value": "node.search_customers_id.data.customers[0].id" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "customerId must be a positive integer.",
      "Success data includes customer fields, policies, and totalPremium.",
    ].join("\n"),
  },

  agencyzoom_add_customer_note: {
    label: "AgencyZoom Add Customer Note",
    icon: "note-sticky" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Add a note to an AgencyZoom customer",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.message", type: "string" },
      { name: "data.id", type: "number" },
      { name: "data.result", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      customerId: omegaParam(z.number().int().positive()),
      note: omegaString,
    }),
    usageExample: `{
  "customerId": { "type": "cel", "value": "node.get_customer_id.data.id" },
  "note": { "type": "template", "value": "Follow-up call completed for {{node.start_id.payload.name}}" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "customerId must be a positive integer. note must be non-empty.",
    ].join("\n"),
  },

  agencyzoom_search_leads: {
    label: "AgencyZoom Search Leads",
    icon: "users" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom leads with optional filters",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.total_count", type: "number" },
      { name: "data.page", type: "number" },
      { name: "data.page_size", type: "number" },
      { name: "data.quote_amount", type: "number" },
      { name: "data.leads", type: "array", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(100)).optional(),
      sort: omegaParam(z.enum(AGENCY_ZOOM_LEAD_SORT_VALUES)).optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_LEAD_STATUS_VALUES)).optional(),
      assignedTo: omegaParam(z.number().int()).optional(),
      leadSourceId: omegaParam(z.number().int()).optional(),
      customerName: omegaString.optional(),
      customerEmail: omegaString.optional(),
      customerPhone: omegaString.optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
      soldPeriod: omegaParam(z.enum(AGENCY_ZOOM_LEAD_SOLD_PERIOD_VALUES)).optional(),
      workflowId: omegaParam(z.number().int()).optional(),
      workflowStageId: omegaString.optional(),
      lastActivityEarliestDate: omegaString.optional(),
      lastActivityLatestDate: omegaString.optional(),
    }),
    usageExample: `{
  "pageSize": { "type": "literal", "value": 25 },
  "order": { "type": "literal", "value": "desc" },
  "status": { "type": "literal", "value": "new" },
  "customerName": { "type": "literal", "value": "Jane" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "All search filters are optional; omit unset filters rather than sending empty values.",
      "pageSize must be between 1 and 100. order must be asc or desc.",
      "sort must be one of: firstname, lastname, lastEnterStageDate, lastActivityDate.",
      "status must be one of: new, quoted, won, lost, contacted, expired.",
      "soldPeriod must be one of: today, yesterday, mtd, lastmonth, ytd, lastyear.",
      "workflowStageId is a slash-delimited list of stage ids (e.g. stg2/stg3).",
      "startDate, endDate, lastActivityEarliestDate, and lastActivityLatestDate must be valid ISO date-time strings.",
    ].join("\n"),
  },

  agencyzoom_get_lead: {
    label: "AgencyZoom Get Lead",
    icon: "user" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Fetch a single AgencyZoom lead by ID",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data", type: "object", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      leadId: omegaParam(z.number().int().positive()),
    }),
    usageExample: `{
  "leadId": { "type": "cel", "value": "node.search_leads_id.data.leads[0].id" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "leadId must be a positive integer.",
      "Success data includes lead fields, opportunities, quotes, and business details.",
    ].join("\n"),
  },

  agencyzoom_add_lead_note: {
    label: "AgencyZoom Add Lead Note",
    icon: "note-sticky" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Add a note to an AgencyZoom lead",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.message", type: "string" },
      { name: "data.id", type: "number" },
      { name: "data.result", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      leadId: omegaParam(z.number().int().positive()),
      note: omegaString,
    }),
    usageExample: `{
  "leadId": { "type": "cel", "value": "node.get_lead_id.data.id" },
  "note": { "type": "template", "value": "Follow-up call completed for {{node.start_id.payload.name}}" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "leadId must be a positive integer. note must be non-empty.",
    ].join("\n"),
  },

  agencyzoom_search_tasks: {
    label: "AgencyZoom Search Tasks",
    icon: "list-check" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom tasks with optional filters",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.total_count", type: "number" },
      { name: "data.page", type: "number" },
      { name: "data.page_size", type: "number" },
      { name: "data.outstanding_count", type: "number" },
      { name: "data.tasks", type: "array", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(200)).optional(),
      sort: omegaString.optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CATEGORY_VALUES)).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_TASK_STATUS_VALUES)).optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      period: omegaParam(z.enum(AGENCY_ZOOM_TASK_PERIOD_VALUES)).optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
    }),
    usageExample: `{
  "pageSize": { "type": "literal", "value": 25 },
  "order": { "type": "literal", "value": "desc" },
  "status": { "type": "literal", "value": "open" },
  "period": { "type": "literal", "value": "today" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "All search filters are optional; omit unset filters rather than sending empty values.",
      "pageSize must be between 1 and 200. order must be asc or desc.",
      "type must be sales or onboarding.",
      "status must be open or done. cancelled appears in Omega's JSON schema enum but is rejected by CheckInput on search (response-only).",
      "period must be one of: past-due-rotting, past-due, remainder-week, next-week, today.",
      "startDate and endDate must be valid ISO date-time strings.",
    ].join("\n"),
  },

  agencyzoom_complete_task: {
    label: "AgencyZoom Complete Task",
    icon: "circle-check" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Mark an AgencyZoom task complete",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.message", type: "string" },
      { name: "data.id", type: "number" },
      { name: "data.result", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      taskId: omegaParam(z.number().int().positive()),
    }),
    usageExample: `{
  "taskId": { "type": "cel", "value": "node.search_tasks_id.data.tasks[0].id" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "taskId must be a positive integer.",
      "Prefer this node over update_task when only marking a task done.",
    ].join("\n"),
  },

  agencyzoom_create_task: {
    label: "AgencyZoom Create Task",
    icon: "plus" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create an AgencyZoom task",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.message", type: "string" },
      { name: "data.id", type: "number" },
      { name: "data.result", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      title: omegaParam(z.string().min(1)),
      dueDatetime: omegaString.optional(),
      comments: omegaString.optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      contactEmail: omegaString.optional(),
      customerId: omegaParam(z.number().int()).optional(),
      leadId: omegaParam(z.number().int()).optional(),
      duration: omegaParam(z.number().int()).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES)).optional(),
      invitees: z.union([z.array(omegaString), omegaCel]).optional(),
      timeSpecific: omegaParam(z.boolean()).optional(),
    }),
    usageExample: `{
  "title": { "type": "literal", "value": "Follow up with lead" },
  "assigneeId": { "type": "literal", "value": 6818 },
  "type": { "type": "literal", "value": "call" },
  "leadId": { "type": "cel", "value": "node.search_leads_id.data.leads[0].id" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "title must be non-empty. All other fields are optional.",
      "type is the action kind: todo, email, call, or meeting (distinct from search type sales/onboarding).",
      "dueDatetime uses AgencyZoom format YYYY-MM-DD HH:mm:ss and is passed through verbatim.",
      "invitees accepts a list of email strings or a single CEL expression resolving to string[].",
      "Link the task via customerId, leadId, or contactEmail.",
    ].join("\n"),
  },

  agencyzoom_update_task: {
    label: "AgencyZoom Update Task",
    icon: "pen-to-square" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Update an AgencyZoom task (partial update)",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.message", type: "string" },
      { name: "data.id", type: "number" },
      { name: "data.result", type: "boolean" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      taskId: omegaParam(z.number().int().positive()),
      title: omegaParam(z.string().min(1)).optional(),
      dueDatetime: omegaString.optional(),
      comments: omegaString.optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      contactEmail: omegaString.optional(),
      customerId: omegaParam(z.number().int()).optional(),
      leadId: omegaParam(z.number().int()).optional(),
      duration: omegaParam(z.number().int()).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES)).optional(),
      invitees: z.union([z.array(omegaString), omegaCel]).optional(),
      timeSpecific: omegaParam(z.boolean()).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_TASK_STATUS_VALUES)).optional(),
    }),
    usageExample: `{
  "taskId": { "type": "cel", "value": "node.search_tasks_id.data.tasks[0].id" },
  "status": { "type": "literal", "value": "done" }
}`,
    promptNotes: [
      "Requires an active AgencyZoom connection for the organization.",
      "taskId must be a positive integer. title, when supplied, must be non-empty.",
      "Partial update — only supplied fields are sent upstream.",
      "status must be open or done when supplied. cancelled appears in Omega's JSON schema enum but is rejected by CheckInput on update (response-only).",
      "Use complete_task instead when only marking a task done.",
    ].join("\n"),
  },

  agencyzoom_create_lead: {
    label: "AgencyZoom Create Lead",
    icon: "user-plus" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create a personal-lines lead in AgencyZoom",
    hasFailureRoute: true,
    outputs: AGENCY_ZOOM_OUTCOME_OUTPUTS,
    parametersSchema: z.object({
      ...agencyZoomLeadSharedShape,
      firstname: omegaParam(z.string().min(1)),
      pipelineId: omegaAgencyZoomSelectedId,
      stageId: omegaAgencyZoomSelectedId,
    }),
    usageExample: `{
  "firstname": { "type": "literal", "value": "Dana" },
  "pipelineId": { "type": "literal", "value": 311 },
  "stageId": { "type": "literal", "value": 2907 },
  "email": { "type": "cel", "value": "node.start.payload.email" }
}`,
    promptNotes: [
      AGENCY_ZOOM_LEAD_CONNECTION_NOTE,
      "Required: firstname, pipelineId, stageId. Everything else is optional.",
      "pipelineId and stageId must both name a real selection, and stageId must be a stage that belongs to that pipeline.",
      ...AGENCY_ZOOM_LEAD_SHARED_PROMPT_NOTES,
    ].join("\n"),
  },

  agencyzoom_create_business_lead: {
    label: "AgencyZoom Create Business Lead",
    icon: "building" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create a commercial lead in AgencyZoom",
    hasFailureRoute: true,
    outputs: AGENCY_ZOOM_OUTCOME_OUTPUTS,
    parametersSchema: z.object({
      ...agencyZoomLeadSharedShape,
      pipelineId: omegaAgencyZoomSelectedId,
      stageId: omegaAgencyZoomSelectedId,
      firstname: omegaString.optional(),
      name: omegaString.optional(),
      contactName: omegaString.optional(),
      dba: omegaString.optional(),
      fein: omegaString.optional(),
      businessEntity: omegaString.optional(),
      businessClassification: omegaString.optional(),
      businessClassificationId: omegaParam(z.number().int().positive()).optional(),
      yearBusinessStarted: omegaParam(z.number().int().min(1800).max(2200)).optional(),
      numberOfEmployees: omegaParam(z.number().int().min(0)).optional(),
      annualRevenue: omegaParam(z.number().int().min(0)).optional(),
      payroll: omegaParam(z.number().int().min(0)).optional(),
    }),
    usageExample: `{
  "name": { "type": "literal", "value": "Riverside Roofing LLC" },
  "pipelineId": { "type": "literal", "value": 311 },
  "stageId": { "type": "literal", "value": 2907 },
  "contactName": { "type": "literal", "value": "Dana Reyes" }
}`,
    promptNotes: [
      AGENCY_ZOOM_LEAD_CONNECTION_NOTE,
      "Required: pipelineId, stageId. Everything else is optional.",
      "Either firstname or name must be supplied — a commercial lead needs an identity, and the business name alone is enough.",
      "pipelineId and stageId must both name a real selection, and stageId must be a stage that belongs to that pipeline.",
      "Commercial-only fields: name, contactName, dba, fein, businessEntity (free text), businessClassification, businessClassificationId, yearBusinessStarted (1800-2200), numberOfEmployees, annualRevenue, payroll.",
      ...AGENCY_ZOOM_LEAD_SHARED_PROMPT_NOTES,
    ].join("\n"),
  },

  momentum_search_insureds: {
    label: "Momentum Search Insureds",
    icon: "users" as IconName,
    description: "Search Momentum insureds and prospects",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.insureds", type: "array", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      name: omegaString.optional(),
      address: omegaString.optional(),
      email: omegaString.optional(),
      phone: omegaString.optional(),
      insuredId: omegaString.optional(),
      customerId: omegaString.optional(),
      databaseId: omegaUuid.optional(),
    }),
    usageExample: `{
  "name": { "type": "literal", "value": "Prospect, Kevin" }
}`,
    promptNotes: [
      "Requires an active Momentum connection for the organization.",
      "At least one filter is required: name, address, email, phone, insuredId, customerId, or databaseId. Omit unset filters rather than sending empty values.",
      "Returns at most 10 matches; Momentum silently drops the rest, so narrow the filters for a broad search.",
      "name matches the display name, which reads \"Last, First\" for a person.",
      "A result row does not say whether it is an insured or a prospect. Follow with momentum_get_insured (using data.insureds[0].database_id) when that matters.",
      "databaseId is a UUID.",
    ].join("\n"),
  },

  momentum_get_insured: {
    label: "Momentum Get Insured",
    icon: "user" as IconName,
    description: "Fetch a single Momentum insured or prospect by database ID",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data", type: "object", isSchemaless: true },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.status", type: "number" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
    }),
    usageExample: `{
  "insuredDatabaseId": { "type": "cel", "value": "node.search_insureds_id.data.insureds[0].database_id" }
}`,
    promptNotes: [
      "Requires an active Momentum connection for the organization.",
      "insuredDatabaseId is a UUID, usually data.insureds[n].database_id from momentum_search_insureds.",
      "Success data.type is Insured or Prospect. An id that matches nothing takes the failure route with status 404.",
    ].join("\n"),
  },

  momentum_add_note: {
    label: "Momentum Add Note",
    icon: "note-sticky" as IconName,
    description: "Add a note to a Momentum insured or prospect",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.status", type: "number" },
      { name: "data.database_id", type: "string" },
      { name: "data.message", type: "string" },
      { name: "data.type", type: "string" },
      { name: "data.title", type: "string" },
      { name: "data.detail", type: "string" },
      { name: "data.instance", type: "string" },
    ],
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
      subject: omegaString,
      type: omegaString.optional(),
    }),
    usageExample: `{
  "insuredDatabaseId": { "type": "cel", "value": "node.search_insureds_id.data.insureds[0].database_id" },
  "subject": { "type": "template", "value": "Called {{node.start_id.payload.name}} about renewal" }
}`,
    promptNotes: [
      "Requires an active Momentum connection for the organization.",
      "insuredDatabaseId is a UUID. subject is the note text and must be non-empty.",
      "type is a note type name from the agency's own list; omit it for Momentum's default. Momentum accepts an unknown type silently, so never invent one.",
      "Success data.database_id is the new note's id. The node is not retried, so a failure never writes a duplicate note.",
    ].join("\n"),
  },

  fill_pdf_form: {
    label: "Fill PDF Form",
    icon: "file-pen" as IconName,
    description: "Fill an AcroForm PDF using upstream data",
    hasFailureRoute: true,
    outputs: [
      successFlagOutput,
      { name: "data.file", type: "object" },
      { name: "data.file.id", type: "string" },
      { name: "data.file.file_name", type: "string" },
      { name: "data.file.media_type", type: "string" },
      { name: "data.file.size_bytes", type: "number" },
      { name: "data.file.storage_path", type: "string" },
      { name: "data.field_values", type: "object", isSchemaless: true },
      { name: "data.template_field_count", type: "number" },
      { name: "data.filled_field_count", type: "number" },
      { name: "data.unapplied_field_names", type: "array" },
      ...failureErrorOutputs,
    ],
    parametersSchema: z
      .object({
        template: omegaFilePointer,
        // sourceData is `Value<object>` on the wire and almost always a CEL
        // reference to an upstream node's data (e.g. node.docAnalysis.data
        // or node.parse_csv.data.rows[i]). Lock to CEL like document_analysis.
        sourceData: omegaCel,
        fileName: omegaString,
        hints: omegaString.optional(),
        flatten: omegaParam(z.boolean()).optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's CheckInput() literal validations. Skip for CEL/template
        // because resolved values are re-validated by `[ValidateResolvedInput]`.
        if (value.fileName.type === "literal") {
          const literalName = (value.fileName.value ?? "").trim();
          if (literalName === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message: "'File name' cannot be empty.",
            });
          } else if (!/\.[^./\\]+$/.test(literalName)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message:
                "'File name' must include an extension (e.g. filled.pdf).",
            });
          } else if (/[/\\]/.test(literalName)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message:
                "'File name' cannot contain path separators (/ or \\).",
            });
          }
        }
        if (value.hints && value.hints.type === "literal") {
          if ((value.hints.value ?? "").trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["hints"],
              message: "'Hints' must not be empty — omit the field instead.",
            });
          }
        }
      }),
    usageExample: `{
  "template": { "type": "cel", "value": "node.<startNodeId>.files[0]" },
  "sourceData": { "type": "cel", "value": "node.docAnalysis.data" },
  "fileName": { "type": "literal", "value": "filled-acord-25.pdf" },
  "hints": { "type": "literal", "value": "Tx27=Producer Phone, CheckBox1a=Occurrence indicator" },
  "flatten": { "type": "literal", "value": true }
}`,
    promptNotes: [
      "template MUST be an AcroForm-fillable PDF; XFA-only PDFs are rejected at runtime.",
      "template is a Value<FilePointer> ({ storage_path, media_type }). Objects with extra keys (e.g. a FileReference with id/file_name/size_bytes) are accepted — only storage_path and media_type are read.",
      "Three canonical ways to source the template:",
      "  (A) User uploads it on the Start node — use CEL `node.<startNodeId>.files[0]`. The Start node must have file uploads enabled. The start node's id is a UUID (e.g. node.fe15ef37-bef8-...), not the literal string 'start'. Uploaded files appear as node.<startNodeId>.files[i] in upload order.",
      "  (B) Pulled from external storage — use a Google Drive Download or SFTP Download node and reference `node.<downloadNodeId>.data.file`.",
      "  (C) Produced by an earlier node — reference that node's `data.file` (it must already be a fillable PDF; Create Text File outputs plain text and is not suitable as a template).",
      "sourceData accepts any upstream payload — pass a CEL reference like node.docAnalysis.data, node.parse_csv.data.rows[0], or node.<startNodeId>.payload. The LLM maps this onto the template's fields. Locked to CEL because it must reference upstream output.",
      "hints is an optional free-form cheat sheet to disambiguate cryptic AcroForm field names. Format is open — comma-separated `FieldName=Meaning` pairs work well (e.g. 'Tx27=Producer Phone, CheckBox1a=Occurrence indicator'). Omit when field names are already self-describing.",
      "flatten bakes filled values into the page content streams (recipient cannot edit). Off by default.",
      String.raw`fileName must include an extension and cannot contain path separators (/ or \). It is the OUTPUT filename, not the template's filename.`,
      "On success, data.file is a FileReference usable by Send Email attachments, SFTP Upload, Google Drive Upload, etc. data.fieldValues is the LLM's sanitized mapping; data.unappliedFieldNames lists template fields the LLM did not fill.",
      "Common pattern: Start (with file upload) → Document Analysis (extract data) → Fill PDF Form (template = node.<startNodeId>.files[0], sourceData = node.docAnalysis.data) → Send Email (attach data.file).",
    ].join("\n"),
  },

  noop: {
    label: "Noop",
    icon: "circle",
    description: "Empty placeholder for branches",
    isPlaceholder: true,
  },

  sub_graph: {
    label: "Sub Graph",
    icon: "object-group" as IconName,
    description: "Group nodes into a reusable nested workflow",
    isContainer: true,
    promptNotes: [
      "Container — newly added sub_graphs come pre-wired with `sub_graph_start` (Entry) → `sub_graph_end` (Exit). Populate the body via `add_node_in_container` (preferred) or by calling `add_node` with `after_node_id` set to an inner node ID.",
      "All paths inside MUST converge to the single `sub_graph_end`. Never add `end` nodes inside a sub_graph.",
    ].join("\n"),
  },

  sub_graph_start: {
    label: "Entry",
    icon: "right-to-bracket" as IconName,
    description: "SubGraph entry point",
    isTrigger: true,
    isPlaceholder: true,
  },

  sub_graph_end: {
    label: "Exit",
    icon: "right-from-bracket" as IconName,
    description: "SubGraph exit point",
    isTerminal: true,
    isPlaceholder: true,
  },

  map: {
    label: "Map",
    icon: "arrow-right-arrow-left" as IconName,
    description:
      "Transform every item of a list with per-item expressions — a For Each plus per-item Data Transform in one step",
    hasFailureRoute: true,
    // Hidden from regular users while the node is rolled out — remove to GA.
    superAdminOnly: true,
    outputs: [
      successFlagOutput,
      { name: "data.items", type: "array" },
      { name: "data.count", type: "number" },
      { name: "data.error", type: "string" },
      { name: "data.item_index", type: "number" },
      { name: "data.projection_key", type: "string" },
    ],
    parametersSchema: z
      .object({
        items: omegaParam(z.array(z.unknown())),
        projection: z.record(omegaParam(z.unknown())).optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's Map.CheckInput (publish-time validation) so users get
        // immediate feedback instead of a backend rejection on save.
        if (
          value.items.type === "literal" &&
          Array.isArray(value.items.value) &&
          value.items.value.length > MAP_MAX_ITEMS
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["items"],
            message: `'Items' has ${value.items.value.length} items, which exceeds the maximum of ${MAP_MAX_ITEMS}.`,
          });
        }

        if (!value.projection) return;

        const normalizedKeys = new Map<string, string>();
        for (const [key, param] of Object.entries(value.projection)) {
          const normalized = normalizeCelFieldName(key);
          if (!normalized) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `The projection field name '${key}' normalizes to an empty name.`,
            });
            continue;
          }
          const existing = normalizedKeys.get(normalized);
          if (existing) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `Projection field collision: '${existing}' and '${key}' are treated as the same output field ('${normalized}'). Rename one of them.`,
            });
          } else {
            normalizedKeys.set(normalized, key);
          }

          // Projection expressions run per item inside the activity with only
          // node.item / node.index in scope — references to other nodes or the
          // run-scoped config/sys/secret namespaces fail at runtime.
          if (param.type !== "cel" && param.type !== "template") continue;
          const expression = String(param.value);
          for (const ref of findForbiddenProjectionReferences(expression)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `The projection expression for '${key}' references '${ref}', which is not available inside a projection. Projection expressions run once per item and can only read node.item and node.index.`,
            });
          }
        }
      }),
    usageExample: `{
  "items": { "type": "cel", "value": "node.parse_csv.data.rows" },
  "projection": {
    "full_name": { "type": "template", "value": "{{ node.item.first_name }} {{ node.item.last_name }}" },
    "email": { "type": "cel", "value": "node.item.email" },
    "row_number": { "type": "cel", "value": "node.index + 1" }
  }
}`,
    promptNotes: [
      "Transforms a list in a single activity — use it instead of a foreach whose body is only a Data Transform.",
      "items must resolve to an array (e.g. node.parse_csv.data.rows). Hard cap: 10,000 items.",
      "projection maps output field name → expression evaluated once per item. ONLY node.item and node.index are in scope — referencing other nodes or config.*/sys.*/secret.* is rejected at publish time. Anything a projection needs must be merged into the items upstream.",
      "Omit projection (or leave it empty) to pass items through unchanged (identity projection).",
      "Projection field names are normalized to snake_case for CEL access; names that collide after normalization are rejected.",
      "On success, data.items is the projected list (input order preserved) and data.count its length. On failure, data.error describes the problem and data.item_index / data.projection_key locate a per-item expression failure.",
      "Map is NOT a container — it has no inner graph. For per-item side effects (calls, emails, HTTP), use foreach instead.",
    ].join("\n"),
  },

  foreach: {
    label: "For Each",
    icon: "repeat" as IconName,
    description: "Iterate over a collection, running inner graph for each item",
    isContainer: true,
    hasFailureRoute: true,
    outputs: [
      { name: "results", type: "array" },
      { name: "count", type: "number" },
      { name: "is_success", type: "boolean" },
    ],
    parametersSchema: z.object({
      collection: omegaParam(z.array(z.unknown())),
      collectResults: omegaParam(z.boolean()).optional(),
    }),
    containerScope: {
      variables: [
        {
          name: "_each_.item",
          type: "object",
          description: "Current iteration item",
        },
        {
          name: "_each_.index",
          type: "number",
          description: "0-based iteration index",
        },
      ],
    },
    promptNotes: [
      "Container — populate the body with inner nodes via `add_node_in_container` (preferred) or `add_node` targeting an inner node ID.",
      "All paths inside MUST converge to the single `sub_graph_end`. Never add `end` nodes inside a foreach.",
      'Inside the inner graph, reference iteration variables as `node.<this-foreach-id>._each_.item` (CEL) or `{{ node.<this-foreach-id>._each_.item }}` (template). Do NOT use bare names like `item`, `currentItem`, or `_each_.item` alone — the path MUST be fully qualified with `node.<foreach-id>`. The Current Workflow section lists the concrete ID-bound paths under each foreach.',
      "The `collection` parameter takes an array reference (e.g. `node.start.payload.names`) — NOT a single item. The For Each runs the inner graph once per array element.",
      "Outputs (`results`, `count`, `is_success`) are available to nodes AFTER the foreach via `node.<foreach-id>.results` etc.",
      "`results` is EMPTY unless `collectResults: true`. It defaults to false to keep large fan-outs under Temporal's payload limit. Set `collectResults: true` whenever a downstream node reads `node.<foreach-id>.results`; leave it off otherwise.",
    ].join("\n"),
  },
};

// =============================================================================
// Derived Exports — NodePicker UI
// =============================================================================

/** Node definition shape used by the NodePicker and other UI components */
export interface NodeDefinitionItem {
  type: string;
  label: string;
  icon: IconName;
  /** Optional brand SVG path; when set, consumers render an <img> instead of the FontAwesome icon. */
  iconUrl?: string;
  description: string;
  superAdminOnly?: boolean;
}

/** All visible node definitions for the NodePicker (excludes noop/ghost) */
export const NODE_DEFINITIONS: NodeDefinitionItem[] = Object.entries(NODE_REGISTRY)
  .filter(([, def]) => !def.isPlaceholder)
  .map(([type, def]) => ({
    type,
    label: def.label,
    icon: def.icon,
    iconUrl: def.iconUrl,
    description: def.description,
    superAdminOnly: def.superAdminOnly,
  }));

/** Node definitions that can be added from handles/edges (excludes Start) */
export const ADDABLE_NODE_DEFINITIONS: NodeDefinitionItem[] = NODE_DEFINITIONS.filter(
  (node) => node.type !== "start"
);

export function getTranslatedNodeDefinitionLabel(type: string, t: TFunction): string {
  switch (type) {
    case NODE_TYPE_KEYS.Start:
      return t("nodeSettings.nodeTypes.start");
    case NODE_TYPE_KEYS.End:
      return t("nodeSettings.nodeTypes.end");
    case NODE_TYPE_KEYS.Delay:
      return t("nodeSettings.nodeTypes.delay");
    case NODE_TYPE_KEYS.RandomNumber:
      return t("nodeSettings.nodeTypes.randomNumber");
    case NODE_TYPE_KEYS.SlackMessage:
      return t("nodeSettings.nodeTypes.slackMessage");
    case "slack_direct_message":
      return t("slackMessage.directMessage");
    case "slack_post_channel_message":
      return t("slackMessage.channelMessage");
    case NODE_TYPE_KEYS.Conditional:
      return t("nodeSettings.nodeTypes.conditional");
    case NODE_TYPE_KEYS.Check:
      return t("nodeSettings.nodeTypes.check");
    case NODE_TYPE_KEYS.OutboundCall:
      return t("nodeSettings.nodeTypes.outboundCall");
    case NODE_TYPE_KEYS.HttpRequest:
      return t("nodeSettings.nodeTypes.httpRequest");
    case NODE_TYPE_KEYS.SendEmail:
      return t("nodeSettings.nodeTypes.sendEmail");
    case NODE_TYPE_KEYS.SendSms:
      return t("nodeSettings.nodeTypes.sendSms");
    case NODE_TYPE_KEYS.DataTransform:
    case "pipe":
      return t("nodeSettings.nodeTypes.dataTransform");
    case NODE_TYPE_KEYS.AddContact:
      return t("nodeSettings.nodeTypes.addContact");
    case NODE_TYPE_KEYS.AddBulkContacts:
      return t("nodeSettings.nodeTypes.addBulkContacts");
    case NODE_TYPE_KEYS.CreateContactList:
      return t("nodeSettings.nodeTypes.createContactList");
    case NODE_TYPE_KEYS.Ghost:
    case "noop":
      return t("nodeSettings.nodeTypes.ghost");
    case NODE_TYPE_KEYS.StartCampaign:
      return t("nodeSettings.nodeTypes.startCampaign");
    case NODE_TYPE_KEYS.CreateCampaign:
      return t("nodeSettings.nodeTypes.createCampaign");
    case NODE_TYPE_KEYS.AddContactToCampaign:
      return t("nodeSettings.nodeTypes.addContactToCampaign");
    case NODE_TYPE_KEYS.AddContactsToCampaignBulk:
      return t("nodeSettings.nodeTypes.addContactsToCampaignBulk");
    case NODE_TYPE_KEYS.AddContactToContactList:
      return t("nodeSettings.nodeTypes.addContactToContactList");
    case NODE_TYPE_KEYS.Chaos:
      return t("nodeSettings.nodeTypes.chaos");
    case NODE_TYPE_KEYS.Agent:
      return t("nodeSettings.nodeTypes.agent");
    case NODE_TYPE_KEYS.RunSkill:
      return t("nodeSettings.nodeTypes.runSkill");
    case NODE_TYPE_KEYS.SubGraph:
      return t("nodeSettings.nodeTypes.subGraph");
    case NODE_TYPE_KEYS.SubGraphStart:
      return t("subGraph.entry");
    case NODE_TYPE_KEYS.SubGraphEnd:
      return t("subGraph.exit");
    case NODE_TYPE_KEYS.ForEach:
      return t("nodeSettings.nodeTypes.forEach");
    case NODE_TYPE_KEYS.DocumentAnalysis:
      return t("nodeSettings.nodeTypes.documentAnalysis");
    case NODE_TYPE_KEYS.JotFormSubmitForm:
      return t("nodeSettings.nodeTypes.jotFormSubmitForm");
    case NODE_TYPE_KEYS.SftpUploadFile:
      return t("nodeSettings.nodeTypes.sftpUploadFile");
    case NODE_TYPE_KEYS.SftpDownloadFile:
      return t("nodeSettings.nodeTypes.sftpDownloadFile");
    case NODE_TYPE_KEYS.SftpListDirectory:
      return t("nodeSettings.nodeTypes.sftpListDirectory");
    case NODE_TYPE_KEYS.GoogleDriveListFiles:
      return t("nodeSettings.nodeTypes.googleDriveListFiles");
    case NODE_TYPE_KEYS.GoogleDriveDownloadFile:
      return t("nodeSettings.nodeTypes.googleDriveDownloadFile");
    case NODE_TYPE_KEYS.GoogleDriveUploadFile:
      return t("nodeSettings.nodeTypes.googleDriveUploadFile");
    case NODE_TYPE_KEYS.GoogleSheetsAppendRows:
      return t("nodeSettings.nodeTypes.googleSheetsAppendRows");
    case NODE_TYPE_KEYS.AgencyZoomSearchCustomers:
      return t("nodeSettings.nodeTypes.agencyZoomSearchCustomers");
    case NODE_TYPE_KEYS.AgencyZoomGetCustomer:
      return t("nodeSettings.nodeTypes.agencyZoomGetCustomer");
    case NODE_TYPE_KEYS.AgencyZoomAddCustomerNote:
      return t("nodeSettings.nodeTypes.agencyZoomAddCustomerNote");
    case NODE_TYPE_KEYS.AgencyZoomSearchLeads:
      return t("nodeSettings.nodeTypes.agencyZoomSearchLeads");
    case NODE_TYPE_KEYS.AgencyZoomGetLead:
      return t("nodeSettings.nodeTypes.agencyZoomGetLead");
    case NODE_TYPE_KEYS.AgencyZoomAddLeadNote:
      return t("nodeSettings.nodeTypes.agencyZoomAddLeadNote");
    case NODE_TYPE_KEYS.AgencyZoomSearchTasks:
      return t("nodeSettings.nodeTypes.agencyZoomSearchTasks");
    case NODE_TYPE_KEYS.AgencyZoomCompleteTask:
      return t("nodeSettings.nodeTypes.agencyZoomCompleteTask");
    case NODE_TYPE_KEYS.AgencyZoomCreateTask:
      return t("nodeSettings.nodeTypes.agencyZoomCreateTask");
    case NODE_TYPE_KEYS.AgencyZoomUpdateTask:
      return t("nodeSettings.nodeTypes.agencyZoomUpdateTask");
    case NODE_TYPE_KEYS.AgencyZoomCreateLead:
      return t("nodeSettings.nodeTypes.agencyZoomCreateLead");
    case NODE_TYPE_KEYS.AgencyZoomCreateBusinessLead:
      return t("nodeSettings.nodeTypes.agencyZoomCreateBusinessLead");
    case NODE_TYPE_KEYS.MomentumSearchInsureds:
      return t("nodeSettings.nodeTypes.momentumSearchInsureds");
    case NODE_TYPE_KEYS.MomentumGetInsured:
      return t("nodeSettings.nodeTypes.momentumGetInsured");
    case NODE_TYPE_KEYS.MomentumAddNote:
      return t("nodeSettings.nodeTypes.momentumAddNote");
    case NODE_TYPE_KEYS.CreateFile:
      return t("nodeSettings.nodeTypes.createFile");
    case NODE_TYPE_KEYS.ParseCsv:
      return t("nodeSettings.nodeTypes.parseCsv");
    case NODE_TYPE_KEYS.SliceCsv:
      return t("nodeSettings.nodeTypes.sliceCsv");
    case NODE_TYPE_KEYS.CreateCsv:
      return t("nodeSettings.nodeTypes.createCsv");
    case NODE_TYPE_KEYS.GetCallData:
      return t("nodeSettings.nodeTypes.getCallData");
    case NODE_TYPE_KEYS.DownloadCallRecording:
      return t("nodeSettings.nodeTypes.downloadCallRecording");
    case NODE_TYPE_KEYS.WebSearch:
      return t("nodeSettings.nodeTypes.webSearch");
    case NODE_TYPE_KEYS.WebExtract:
      return t("nodeSettings.nodeTypes.webExtract");
    case NODE_TYPE_KEYS.FillPdfForm:
      return t("nodeSettings.nodeTypes.fillPdfForm");
    case NODE_TYPE_KEYS.Map:
      return t("nodeSettings.nodeTypes.map");
    default:
      return t("nodeSettings.nodeTypes.node");
  }
}

// =============================================================================
// Derived Exports — AI System Prompt
// =============================================================================

/**
 * Check if a ZodUnion represents an OmegaParameter (literal | cel | template).
 * Returns the literal option if it is, undefined otherwise.
 */
function findOmegaParamLiteralOption(
  options: readonly z.ZodTypeAny[]
): z.ZodObject<z.ZodRawShape> | undefined {
  if (options.length !== 3) return undefined;

  const hasTypedObject = options.some(
    (o) => o instanceof z.ZodObject && "type" in (o as z.ZodObject<z.ZodRawShape>).shape
  );
  if (!hasTypedObject) return undefined;

  return options.find((o) => {
    if (!(o instanceof z.ZodObject)) return false;
    const shape = (o as z.ZodObject<z.ZodRawShape>).shape;
    if (!("type" in shape) || !("value" in shape)) return false;
    return shape.type instanceof z.ZodLiteral && shape.type.value === "literal";
  }) as z.ZodObject<z.ZodRawShape> | undefined;
}

/**
 * Describe a Zod schema type in human-readable form for the AI prompt.
 * Recursively handles objects, arrays, unions, optionals, records, etc.
 */
function describeZodType(schema: z.ZodTypeAny, depth = 0): string {
  if (depth > 3) return "...";

  if (schema instanceof z.ZodOptional) {
    return `${describeZodType(schema.unwrap(), depth)}?`;
  }

  if (schema instanceof z.ZodUnion) {
    const options = (schema as z.ZodUnion<z.ZodUnionOptions>).options as readonly z.ZodTypeAny[];
    const literalOption = findOmegaParamLiteralOption(options);
    if (literalOption) {
      const valueType = describeZodType(literalOption.shape.value, depth + 1);
      return `OmegaParameter<${valueType}>`;
    }
    return options.map((o) => describeZodType(o, depth + 1)).join(" | ");
  }

  if (schema instanceof z.ZodObject) {
    const shape = (schema as z.ZodObject<z.ZodRawShape>).shape;
    const fields = Object.entries(shape)
      .map(([key, val]) => `${key}: ${describeZodType(val, depth + 1)}`)
      .join(", ");
    return `{ ${fields} }`;
  }

  if (schema instanceof z.ZodArray) return `${describeZodType(schema.element, depth + 1)}[]`;
  if (schema instanceof z.ZodRecord)
    return `Record<string, ${describeZodType(schema.element, depth + 1)}>`;
  if (schema instanceof z.ZodLiteral) return JSON.stringify(schema.value);
  if (schema instanceof z.ZodString) return "string";
  if (schema instanceof z.ZodNumber) return "number";
  if (schema instanceof z.ZodBoolean) return "boolean";
  if (schema instanceof z.ZodUnknown) return "unknown";

  return "any";
}

/**
 * Returns all output fields for a node type, including derived parent paths.
 * E.g., `data.documents` and `data.summary` also yields `data: object`.
 */
export function getNodeOutputsWithParents(type: string): NodeOutputField[] {
  const outputs = NODE_REGISTRY[type]?.outputs ?? [];
  if (outputs.length === 0) return [];
  return injectParentObjectFields(
    outputs,
    (f) => f.name,
    (parent) => ({ name: parent, type: "object" })
  );
}

function formatOutputsForPrompt(outputs: NodeOutputField[]): string {
  return outputs
    .map((f) => {
      const schemaless = f.isSchemaless ? " (schemaless — user defines structure)" : "";
      return `    - ${f.name}: ${f.type}${schemaless}`;
    })
    .join("\n");
}

function formatFlagsForPrompt(def: NodeRegistryEntry): string[] {
  const flags: string[] = [];
  if (def.isTrigger) flags.push("trigger (no incoming edges, max 1 per workflow)");
  if (def.isTerminal) flags.push("terminal (no outgoing edges)");
  if (def.hasFailureRoute) flags.push("has failure route (onFailureEdgeId)");
  if (def.isBranching) flags.push("branching (multiple outgoing edges)");
  if (def.isPlaceholder) flags.push("placeholder (auto-created by tools, not user-addable)");
  return flags;
}

/**
 * Auto-generate the node catalog text for the AI system prompt.
 * Iterates NODE_REGISTRY, describes each type with its parameters, outputs, and flags.
 *
 * @param availableNodeTypes  Node types Omega currently exposes to this org
 *   (from `GET /nodes`, already filtered by per-node feature flags). When
 *   provided, addable nodes Omega doesn't expose are omitted from the catalog
 *   so the AI never suggests a disabled node. Structural nodes (triggers,
 *   terminals, and tool-created placeholders) are always kept so the AI retains
 *   the context it needs to read and edit the graph. `null`/omitted fails open
 *   and describes every node.
 */
export function buildNodeCatalogForPrompt(
  availableNodeTypes?: Set<string> | null
): string {
  return Object.entries(NODE_REGISTRY)
    .filter(([type, def]) => {
      if (!availableNodeTypes) return true;
      if (def.isTrigger || def.isTerminal || def.isPlaceholder) return true;
      return availableNodeTypes.has(type);
    })
    .map(([type, def]) => {
      const lines: string[] = [];
      lines.push(`- **${type}** (${def.label}): ${def.description}`);

      if (def.parametersSchema) {
        lines.push("  Parameters:");
        const shape = unwrapParametersShape(def.parametersSchema);
        const params = Object.entries(shape)
          .map(
            ([name, fieldSchema]) =>
              `    - ${name}: ${describeZodType(fieldSchema as z.ZodTypeAny)}`
          )
          .join("\n");
        lines.push(params);
      }

      const allOutputs = getNodeOutputsWithParents(type);
      if (allOutputs.length > 0) {
        lines.push("  Outputs (for CEL/template expressions — use `node.{id}.{field}`):");
        lines.push(formatOutputsForPrompt(allOutputs));
      }

      if (def.containerScope?.variables.length) {
        lines.push(
          "  Iteration scope variables (auto-injected on every node inside this container — reference as `node.<this-container-id>.<name>`):"
        );
        for (const v of def.containerScope.variables) {
          lines.push(`    - ${v.name}: ${v.type} — ${v.description}`);
        }
      }

      const flags = formatFlagsForPrompt(def);
      if (flags.length > 0) {
        lines.push(`  Flags: ${flags.join(", ")}`);
      }

      if (def.usageExample) {
        lines.push(
          `  Example params:\n  \`\`\`json\n  ${def.usageExample.split("\n").join("\n  ")}\n  \`\`\``
        );
      }

      if (def.promptNotes) {
        const indented = def.promptNotes
          .split("\n")
          .map((l) => "    " + l)
          .join("\n");
        lines.push("  Notes:\n" + indented);
      }

      return lines.join("\n");
    })
    .join("\n\n");
}

// =============================================================================
// Derived Exports — Gemini Tool Schemas
// =============================================================================

/**
 * Node types that the AI can add (excludes start, end, noop, and
 * superAdminOnly types by default).
 * Used as the enum for the add_node tool's node_type parameter.
 *
 * @param options.includeSuperAdminOnly - Include super-admin-only types
 *   (e.g. chaos). Defaults to false so non-admin users can't ask the AI
 *   to create restricted nodes.
 */
export function getAddableNodeTypes(options?: { includeSuperAdminOnly?: boolean }): string[] {
  const includeSuperAdmin = options?.includeSuperAdminOnly ?? false;
  return Object.entries(NODE_REGISTRY)
    .filter(
      ([, def]) =>
        !def.isPlaceholder &&
        !def.isTrigger &&
        !def.isTerminal &&
        (includeSuperAdmin || !def.superAdminOnly)
    )
    .map(([type]) => type);
}

/**
 * All valid node types (including start, end, noop).
 */
export function getAllNodeTypes(): string[] {
  return Object.keys(NODE_REGISTRY);
}

// =============================================================================
// Derived Exports — Validation Helpers
// =============================================================================

/** Check if a node type string is valid */
export function isValidNodeType(type: string): boolean {
  return type in NODE_REGISTRY;
}

/** Get all node types that have failure routes — derived from registry, not hardcoded. */
export function getFailureRouteTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.hasFailureRoute)
    .map(([type]) => type);
}

/** Get all branching node types — derived from registry, not hardcoded. */
export function getBranchingTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.isBranching)
    .map(([type]) => type);
}

/** Check if a node type has a failure route (onFailureEdgeId) */
export function nodeHasFailureRoute(type: string): boolean {
  return NODE_REGISTRY[type]?.hasFailureRoute ?? false;
}

/** Check if a node type is a branching node (split) */
export function nodeIsBranching(type: string): boolean {
  return NODE_REGISTRY[type]?.isBranching ?? false;
}

/** Check if a node type is a trigger (start) */
export function nodeIsTrigger(type: string): boolean {
  return NODE_REGISTRY[type]?.isTrigger ?? false;
}

/** Check if a node type is a terminal (end) */
export function nodeIsTerminal(type: string): boolean {
  return NODE_REGISTRY[type]?.isTerminal ?? false;
}

/** Check if a node type is a container (sub_graph) */
export function nodeIsContainer(type: string): boolean {
  return NODE_REGISTRY[type]?.isContainer ?? false;
}

/**
 * Return the `containerScope` declaration for a container type, or
 * `undefined` if the type is not a container or doesn't expose
 * iteration-scope variables. Drives both the AI prompt's iteration-scope
 * hints and the builder UI's available-variables injection.
 */
export function getContainerScope(type: string): ContainerScope | undefined {
  return NODE_REGISTRY[type]?.containerScope;
}

/**
 * All registered container types that expose iteration-scope variables.
 * Used by the system prompt to enumerate "iteration-injecting containers"
 * generically without naming `foreach` directly.
 */
export function getIterationContainerTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.containerScope?.variables.length)
    .map(([type]) => type);
}

/** Get the default label for a node type */
export function getNodeLabel(type: string): string {
  return NODE_REGISTRY[type]?.label ?? type;
}

// =============================================================================
// Derived Exports — Default Parameter Generation
// =============================================================================

/**
 * Resolve a default raw value for a Zod schema. Mirrors generateDefaultValue's
 * intent and produces a concrete value for every non-optional schema kind we
 * use — used for OmegaParameter literal payloads, where the value field is
 * required.
 *
 * Returns `undefined` only for ZodOptional, which signals the caller (the
 * ZodObject branch below) to omit the field from the default object entirely.
 * That preserves "field is genuinely optional" semantics rather than
 * inventing a fake default for it.
 */
function defaultRawValueForSchema(schema: z.ZodTypeAny): unknown {
  if (schema instanceof z.ZodDefault) return schema._def.defaultValue();
  if (schema instanceof z.ZodOptional) return undefined;
  if (schema instanceof z.ZodNullable) return null;
  if (schema instanceof z.ZodNumber) return 0;
  if (schema instanceof z.ZodBoolean) return false;
  if (schema instanceof z.ZodArray) return [];
  if (schema instanceof z.ZodRecord) return {};
  if (schema instanceof z.ZodObject) {
    const result: Record<string, unknown> = {};
    for (const [key, fieldSchema] of Object.entries(schema.shape)) {
      const v = defaultRawValueForSchema(fieldSchema as z.ZodTypeAny);
      if (v !== undefined) result[key] = v;
    }
    return result;
  }
  if (schema instanceof z.ZodString) {
    const checks = (schema._def as { checks?: Array<{ kind?: string }> }).checks;
    if (checks?.some((c) => c.kind === "uuid")) {
      return "00000000-0000-0000-0000-000000000000";
    }
    return "";
  }
  return "";
}

/**
 * Resolve the default literal value for an OmegaParameter's value schema.
 */
function defaultLiteralForValueSchema(valueSchema: z.ZodTypeAny): {
  type: "literal";
  value: unknown;
} {
  return { type: "literal", value: defaultRawValueForSchema(valueSchema) };
}

/**
 * If the schema is an OmegaParameter union (literal | cel | template),
 * return a default `{ type: "literal", value: ... }`.
 */
function defaultForOmegaParamUnion(schema: z.ZodUnion<z.ZodUnionOptions>): unknown {
  const options = schema.options as readonly z.ZodTypeAny[];
  const literalOption = findOmegaParamLiteralOption(options);
  if (!literalOption) return undefined;
  return defaultLiteralForValueSchema(literalOption.shape.value);
}

/**
 * Generate default Omega parameter values for a Zod field schema.
 *
 * - OmegaParameter unions → `{ type: "literal", value: "" | 0 | false }`
 * - Optional fields → `undefined` (skipped)
 * - Nested objects → recurse
 * - Primitives → empty/zero defaults
 */
function generateDefaultValue(schema: z.ZodTypeAny): unknown {
  if (schema instanceof z.ZodOptional) return undefined;
  if (schema instanceof z.ZodDefault) return schema._def.defaultValue();
  if (schema instanceof z.ZodLiteral) return schema.value;
  if (schema instanceof z.ZodUnion) {
    // ValueList<T> dual form (`z.union([z.array(...), omegaCel])`): default to
    // an empty list (list mode) rather than the OmegaParameter literal path.
    const options = schema.options as readonly z.ZodTypeAny[];
    if (options.some((opt) => opt instanceof z.ZodArray)) return [];
    return defaultForOmegaParamUnion(schema);
  }
  if (schema instanceof z.ZodArray) return [];
  if (schema instanceof z.ZodRecord) return {};
  if (schema instanceof z.ZodString) return "";
  if (schema instanceof z.ZodNumber) return 0;
  if (schema instanceof z.ZodBoolean) return false;

  if (schema instanceof z.ZodObject) {
    return generateDefaultsFromShape(schema.shape);
  }

  return undefined;
}

/**
 * Walk a ZodObject shape and generate default values for each field.
 */
function generateDefaultsFromShape(shape: z.ZodRawShape): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, fieldSchema] of Object.entries(shape)) {
    const val = generateDefaultValue(fieldSchema);
    if (val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

/**
 * Auto-generate default Omega-format parameters for a node type by
 * introspecting its Zod parametersSchema.
 *
 * Returns `undefined` for node types without a schema (end, noop).
 *
 * @example
 *   getDefaultParametersForType("random_number")
 *   // → { min: { type: "literal", value: 0 }, max: { type: "literal", value: 0 } }
 *
 *   getDefaultParametersForType("send_email")
 *   // → { to: { type: "literal", value: "" }, subject: { type: "literal", value: "" }, body: { type: "literal", value: "" } }
 */
export function getDefaultParametersForType(type: string): Record<string, unknown> | undefined {
  const entry = NODE_REGISTRY[type];
  if (!entry?.parametersSchema) return undefined;

  return generateDefaultsFromShape(unwrapParametersShape(entry.parametersSchema));
}

/**
 * parametersSchema may be a bare ZodObject or a ZodEffects wrapping one
 * (when superRefine is used). Both expose the inner shape, but only via
 * different paths — ZodObject directly, ZodEffects through innerType().
 */
function unwrapParametersShape(
  schema: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>
): z.ZodRawShape {
  if (schema instanceof z.ZodEffects) {
    return schema.innerType().shape;
  }
  return schema.shape;
}
